/* Generado por theme/tools/construir_tema.py — NO EDITAR.
   37 módulos del núcleo en un archivo. La fuente son los de
   theme/assets/js/. Lo que carga bajo demanda sigue suelto. */
const __EDI = {};
window.__EDI = __EDI;

__EDI["core-i18n.js"] = (function () {
  'use strict';

  /** Diccionarios cargados por fetch. Ningún texto vive dentro de un componente. */

  const SUPPORTED = ['fr', 'de', 'en', 'es'];
  const FALLBACK = 'fr';

  let current = FALLBACK;
  let dict = {};
  let fallbackDict = {};

  /**
   * Se resuelve cuando el diccionario está cargado.
   *
   * Hace falta porque el editor de CUSTOM se monta con su propio import() en
   * paralelo con app.js: si llamaba a t() antes de que llegara el JSON, pintaba
   * la clave en crudo — se llegó a ver «CUSTOM.SAVED» en la barra del editor.
   */
  let resolveReady;
  const ready = new Promise((resolve) => { resolveReady = resolve; });

  const get = (source, path) =>
    path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), source);

  /**
   * Dónde vive el diccionario de un idioma.
   *
   * En el prototipo, en `/locales/`. En Shopify NO: `assets/` es plano, se
   * sirve desde un CDN y cada archivo lleva su propio `?v=` — así que la URL de
   * `de` no se obtiene cambiando « fr » por « de » en la de al lado. Por eso el
   * layout escribe el mapa ENTERO y aquí sólo se lee.
   *
   * Sin mapa se cae a la ruta del prototipo, que es donde esto nació. Un mapa
   * roto tampoco puede dejar la tienda sin textos: se ignora y se sigue.
   */
  function urlDe(locale) {
    const etiqueta = document.getElementById('edisamp-i18n-urls');
    if (etiqueta) {
      try {
        const mapa = JSON.parse(etiqueta.textContent);
        if (mapa && mapa[locale]) return mapa[locale];
      } catch (error) { /* sigue con la ruta de siempre */ }
    }
    return `/locales/${locale}.json`;
  }

  async function load(locale) {
    const response = await fetch(urlDe(locale));
    if (!response.ok) throw new Error(`locale ${locale} no disponible`);
    return response.json();
  }

  async function init(locale) {
    const requested = SUPPORTED.includes(locale) ? locale : FALLBACK;

    /* UN diccionario, no dos.
     *
     * Antes se pedían siempre el idioma y el de respaldo: 14 KB de más en cada
     * carga, para cubrir claves que no faltan. Los cuatro diccionarios están
     * COMPLETOS —tienen exactamente las mismas claves— y eso no es una
     * esperanza: `construir_tema.py` lo comprueba y falla si alguno se queda
     * corto.
     *
     * El respaldo sólo se pide si el idioma pedido NO llega, que es cuando de
     * verdad hace falta. */
    try {
      dict = await load(requested);
      fallbackDict = dict;
    } catch (error) {
      dict = await load(FALLBACK);
      fallbackDict = dict;
      current = FALLBACK;
      document.documentElement.lang = FALLBACK;
      resolveReady(FALLBACK);
      return FALLBACK;
    }

    current = requested;
    document.documentElement.lang = requested;
    resolveReady(requested);
    return requested;
  }

  /**
   * El nombre de la marca, de una sola fuente.
   *
   * Estaba escrito a mano en cinco cadenas × cuatro idiomas = veinte sitios que
   * había que acordarse de cambiar a la vez. Con el tema camino de venderse eso
   * deja de ser una molestia y pasa a ser un defecto: quien lo instale tiene que
   * poder ponerle SU nombre sin editar cuatro diccionarios.
   *
   * Ahora los diccionarios dicen `{brand}` y el valor sale del `<html>`, que lo
   * escribe el generador desde `BRAND_NAME`. Un nombre, un sitio.
   */
  function marca() {
    return document.documentElement.dataset.marca || '';
  }

  /** t('a11y.bagCount', { count: 2 }) — si falta la clave, cae al idioma base. */
  function t(path, vars) {
    const raw = get(dict, path) ?? get(fallbackDict, path) ?? path;
    /* `{brand}` se resuelve siempre, aunque quien llame no pase variables: es
       del sitio, no del punto de llamada. Y se puede sobrescribir por si algún
       día hace falta. */
    const todas = { brand: marca(), ...(vars || {}) };
    return String(raw).replace(/\{(\w+)\}/g, (match, key) =>
      key in todas ? String(todas[key]) : match
    );
  }

  const locale = () => current;
  const supported = () => SUPPORTED.slice();

  /**
   * Rellena [data-i18n] y [data-i18n-attr="aria-label:clave"] de un árbol.
   *
   * Con [data-i18n-vars] (JSON) se pasan las variables del texto. Sin esto,
   * una clave con {count} se quedaba escrita tal cual en la página en cuanto
   * cambiaba el idioma: el HTML traía el número resuelto y la traducción lo
   * sustituía por la plantilla.
   */
  function apply(root = document) {
    root.querySelectorAll('[data-i18n]').forEach((node) => {
      let vars;
      if (node.dataset.i18nVars) {
        // Un JSON mal escrito no debe tumbar la traducción de la página entera.
        try { vars = JSON.parse(node.dataset.i18nVars); } catch { vars = undefined; }
      }
      node.textContent = t(node.dataset.i18n, vars);
    });
    root.querySelectorAll('[data-i18n-attr]').forEach((node) => {
      node.dataset.i18nAttr.split(',').forEach((pair) => {
        const [attr, key] = pair.split(':').map((part) => part.trim());
        if (attr && key) node.setAttribute(attr, t(key));
      });
    });
  }

  return { ready, init, t, locale, supported, apply };
})();

__EDI["core-dom.js"] = (function () {
  'use strict';

  /** Helpers de DOM. Nada de framework, nada de dependencias. */

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /** Crea un elemento con atributos y children en una sola expresión. */
  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value == null || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'dataset') Object.assign(node.dataset, value);
      else if (key.startsWith('on') && typeof value === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), value);
      } else node.setAttribute(key, value === true ? '' : String(value));
    }
    for (const child of [].concat(children)) {
      if (child == null) continue;
      node.append(child instanceof Node ? child : document.createTextNode(String(child)));
    }
    return node;
  }

  /** true si el usuario ha pedido menos movimiento. */
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Atrapa el foco dentro de un contenedor (menú fullscreen, drawer, modal). */
  function trapFocus(container) {
    const SELECTOR =
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusables = $$(SELECTOR, container).filter((node) => node.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  }

  /** Bloquea el scroll del body compensando el ancho de la barra. */
  function lockScroll(locked) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.toggleAttribute('data-scroll-locked', locked);
    document.body.style.paddingRight = locked && gap > 0 ? `${gap}px` : '';
  }

  return { $, $$, el, prefersReducedMotion, trapFocus, lockScroll };
})();

__EDI["core-motion.js"] = (function () {
  'use strict';
  const { $$, prefersReducedMotion } = __EDI["core-dom.js"];
  /**
   * Movimiento. Principio: contención.
   * Solo dos cosas viven aquí — aparecer al entrar en vista y un parallax muy corto.
   * Ambas se apagan solas con prefers-reduced-motion.
   */


  /** Revela [data-reveal] una sola vez, con retardo escalonado opcional. */
  function observeReveals(root = document) {
    const targets = $$('[data-reveal]', root);
    if (!targets.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((node) => node.setAttribute('data-inview', ''));
      return;
    }

    const mostrar = (node) => {
      if (node.hasAttribute('data-inview')) return;
      node.setAttribute('data-inview', '');
      observer.unobserve(node);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) mostrar(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach((node) => observer.observe(node));

    /* Red de seguridad.
     *
     * Un reveal que oculta TEXTO no puede depender sólo del observador: la
     * entrega del IntersectionObserver se retrasa —y en una pestaña de fondo
     * llega a no llegar—, y entonces media sección se queda en blanco sin que
     * nada esté roto. Comprobado en este entorno: un observador nuevo sobre un
     * elemento visible al 100 % no entregó nada en 1,2 s.
     *
     * Al segundo se mira a mano quién está ya dentro de la ventana y se le
     * enseña. No sustituye al observador: lo cubre. Lo que está más abajo sigue
     * apareciendo al llegar. */
    const rescatar = () => {
      const alto = window.innerHeight || 0;
      for (const node of targets) {
        if (node.hasAttribute('data-inview')) continue;
        const box = node.getBoundingClientRect();
        if (box.top < alto && box.bottom > 0) mostrar(node);
      }
    };
    setTimeout(rescatar, 1000);
  }

  /**
   * Parallax de recorrido corto (§15). El fondo se mueve a 0.85x como máximo
   * y nunca más de `--parallax-max` px. Se apaga con reduced-motion.
   */
  function parallax(element, { factor = 0.15, max = 60 } = {}) {
    if (prefersReducedMotion()) return () => {};

    let ticking = false;

    function update() {
      const rect = element.parentElement.getBoundingClientRect();
      const progress = rect.top / window.innerHeight;
      const offset = Math.max(-max, Math.min(max, -progress * factor * window.innerHeight));
      element.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  /**
   * Lee una duración del sistema de tokens, en milisegundos.
   *
   * El JS necesita saber cuánto dura una transición para encadenar el paso
   * siguiente, y la alternativa —escribir 350 en el módulo— es exactamente lo
   * que el sistema de tokens existe para evitar: al cambiar el token, el CSS
   * cambiaría y el JS no. Con esto hay una sola cifra, y vive en tokens.css.
   *
   * Con `prefers-reduced-motion` los tokens ya valen 1ms, así que esto
   * devuelve 1 sin que nadie tenga que preguntarlo aparte.
   */
  function tokenDuration(name, { element = document.documentElement, fallback = 0 } = {}) {
    const raw = getComputedStyle(element).getPropertyValue(name).trim();
    if (!raw) return fallback;
    if (raw.endsWith('ms')) return parseFloat(raw) || fallback;
    if (raw.endsWith('s')) return (parseFloat(raw) || 0) * 1000 || fallback;
    return parseFloat(raw) || fallback;
  }

  /** Espera. Con movimiento reducido los tokens ya valen 1ms. */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  return { observeReveals, parallax, tokenDuration, wait };
})();

__EDI["core-media-sure.js"] = (function () {
  'use strict';

  /**
   * Ninguna imagen rota delante de un cliente.
   *
   * El icono de imagen rota del navegador es la tripa del entorno asomando:
   * dice « aquí falló algo » con la tipografía de Chrome, dentro de una tienda
   * que se presenta como cuidada. Aparecía por dos motivos triviales:
   *
   *   · <img> sin atributo `src`. No es un img vacío: el navegador resuelve la
   *     URL del documento, se la baja, ve que es HTML y pinta el icono.
   *   · src="" escrito a mano como estado inicial, que hace lo mismo.
   *
   * Aquí no se arregla cada caso: se cierra la clase entera, ahora y para las
   * imágenes que se añadan luego. Gemelo de `media-sure.js` del Studio: la
   * regla es la misma en las dos mitades de EDISAMP.
   *
   * No sustituye a tener la fotografía. Sustituye a enseñar que no la hay.
   */

  const VACIO = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"/%3E';

  /** ¿Esta fuente va a acabar en el icono de rotura? */
  function fuenteInutil(img) {
    const bruto = img.getAttribute('src');
    if (bruto === null || bruto.trim() === '') return true;
    try {
      const u = new URL(bruto, location.href);
      return u.origin === location.origin && u.pathname === location.pathname
             && !/\.(png|jpe?g|webp|avif|gif|svg)$/i.test(u.pathname);
    } catch (e) { return true; }
  }

  function neutralizar(img) {
    /* Una imagen con `data-src` no es asunto de aquí. No está rota: está
       APLAZADA a propósito y tiene dueño, que le pondrá la fuente cuando toque
       y decidirá qué hacer si no llega.

       Meterle el SVG vacío tiene dos consecuencias, las dos malas. Antes de
       tiempo, compite con quien iba a asignarle la buena: dos sistemas
       escribiendo el mismo atributo del mismo nodo, que es el error que ya
       hundió la cámara de scroll en otra propiedad. Y DESPUÉS, cuando la
       fuente buena falla, sustituirla por un SVG que sí decodifica convierte
       un fallo en un éxito: la escena de profundidad se daba por lista con la
       calle vacía porque su `decode()` había resuelto contra el relleno. */
    if (img.dataset.src) return;

    if (img.dataset.imgFallback === 'true') return;
    img.dataset.imgFallback = 'true';
    img.src = VACIO;
    if (!img.alt) img.setAttribute('role', 'presentation');
  }

  /** Una imagen que RECIBE una fuente buena vuelve a ser una imagen. */
  function revivir(img) {
    if (img.dataset.imgFallback === 'true' && !fuenteInutil(img)) {
      delete img.dataset.imgFallback;
    }
  }

  function revisarImagenes(raiz = document) {
    if (!raiz.querySelectorAll) return;
    for (const img of raiz.querySelectorAll('img')) {
      if (fuenteInutil(img)) neutralizar(img); else revivir(img);
    }
  }

  function initMediaSure() {
    // Los errores de carga no burbujean, pero sí se capturan.
    document.addEventListener('error', (e) => {
      const t = e.target;
      if (t && t.tagName === 'IMG' && t.dataset.imgFallback !== 'true') neutralizar(t);
    }, true);

    if ('MutationObserver' in window) {
      new MutationObserver((muts) => {
        for (const m of muts) {
          if (m.type === 'attributes' && m.target.tagName === 'IMG') {
            const img = m.target;
            if (fuenteInutil(img)) neutralizar(img); else revivir(img);
            continue;
          }
          for (const n of m.addedNodes) {
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'IMG') { if (fuenteInutil(n)) neutralizar(n); }
            else revisarImagenes(n);
          }
        }
      }).observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['src'],
      });
    }

    revisarImagenes();
  }

  return { fuenteInutil, neutralizar, revisarImagenes, initMediaSure };
})();

__EDI["core-prefetch.js"] = (function () {
  'use strict';

  /**
   * Precarga la página que el cliente está a punto de abrir.
   *
   * POR QUÉ
   * -------
   * Cada clic en esta tienda es una navegación completa: HTML, hojas, guiones,
   * fotos. Medido en la tienda de verdad, entre el clic y la página pintada
   * pasan segundos — y una parte grande de esa espera es tiempo muerto en el
   * que el navegador todavía no ha PEDIDO nada.
   *
   * Esa espera se puede gastar antes. Entre que el puntero entra en un enlace y
   * el dedo pulsa pasan entre 150 y 300 ms; en táctil, entre `touchstart` y el
   * clic pasan unos 90. Con eso basta para que el HTML ya esté viajando. La
   * página no se abre más rápido: se empieza antes.
   *
   * LO QUE NO HACE
   * --------------
   * No precarga lo que no se puede volver a pedir sin consecuencias: nada de
   * carrito, cuenta, checkout, formularios ni descargas. Un `prefetch` es un GET
   * y un GET no debería cambiar nada, pero hay rutas que sí lo hacen y no somos
   * quién para decidirlo por la tienda.
   *
   * Tampoco precarga con datos ahorrados activados ni en conexiones lentas: ahí
   * el megabyte de más cuesta dinero de verdad.
   */

  const YA = new Set();

  /** Rutas que no se tocan: cambian estado o cuestan dinero. */
  const PROHIBIDO = /\/(cart|account|checkouts?|orders|apps|services|tools|policies)(\/|$|\?)/i;

  function precargable(a) {
    if (!a || !a.href || a.target === '_blank' || a.hasAttribute('download')) return false;
    if (a.dataset.noPrefetch !== undefined) return false;

    let u;
    try { u = new URL(a.href); } catch { return false; }

    if (u.origin !== location.origin) return false;
    if (u.pathname === location.pathname) return false;
    if (PROHIBIDO.test(u.pathname)) return false;
    if (YA.has(u.href)) return false;
    return u;
  }

  function pedir(a) {
    const u = precargable(a);
    if (!u) return;
    YA.add(u.href);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = u.href;
    document.head.appendChild(link);
  }

  function initPrefetch() {
    // Datos ahorrados o red lenta: el cliente ya ha dicho que no quiere esto.
    const red = navigator.connection;
    if (red && (red.saveData || /2g/.test(red.effectiveType || ''))) return;

    // `prefetch` no está en todos los navegadores; sin él, no se hace nada.
    const soporta = document.createElement('link').relList?.supports?.('prefetch');
    if (!soporta) return;

    const alEntrar = (e) => {
      const a = e.target.closest?.('a[href]');
      if (a) pedir(a);
    };

    // `pointerenter` no burbujea; `pointerover` sí, y con un solo oyente basta.
    document.addEventListener('pointerover', alEntrar, { passive: true });
    document.addEventListener('touchstart', alEntrar, { passive: true });
    document.addEventListener('focusin', alEntrar, { passive: true });
  }

  return { initPrefetch };
})();

__EDI["components-card-porte.js"] = (function () {
  'use strict';

  /**
   * El control « Porté » de las tarjetas — el equivalente táctil del hover.
   *
   * En escritorio, pasar el ratón por una tarjeta cambia la prenda sola por la
   * prenda puesta. En un teléfono no hay hover, y la segunda fotografía estaba
   * sencillamente escondida con `display: none`: la mitad de las visitas no
   * llegaba a ver la única imagen que responde a « ¿cómo me queda? ».
   *
   * Un gesto no lo arreglaba. Mantener pulsado no lo descubre nadie y encima
   * compite con el enlace de la tarjeta, que es lo que el dedo va a tocar. Así
   * que es un botón, se ve, y sólo existe donde hay foto de modelo de verdad
   * —lo decide el generador, no este archivo—.
   *
   * Un solo oyente delegado en la rejilla: hay hasta veinte tarjetas por página
   * y veinte oyentes es basura que además hay que recoger al filtrar.
   */

  function initCardPorte(raiz = document) {
    /* Fase de CAPTURA, no de burbuja.
       El botón vive dentro del enlace de la tarjeta. Escuchando en burbuja,
       este manejador corre DESPUÉS de cualquier oyente del propio enlace: la
       navegación acaba impidiéndose, sí, pero para entonces el enlace ya ha
       reaccionado —analítica, prefetch, lo que haya— a un clic que no era
       suyo. En captura se detiene antes de que nadie más lo vea. */
    raiz.addEventListener('click', (e) => {
      const boton = e.target.closest('[data-card-porte]');
      if (!boton) return;

      e.preventDefault();
      e.stopPropagation();

      const card = boton.closest('.card');
      if (!card) return;

      const puesta = card.dataset.showing !== 'secondary';
      if (puesta) card.dataset.showing = 'secondary';
      else delete card.dataset.showing;

      boton.setAttribute('aria-pressed', String(puesta));
      /* Las dos etiquetas vienen del HTML, ya traducidas por el generador. No
         se piden a `t()` al pulsar: el diccionario llega por fetch, y un clic
         antes de que llegue escribiría « experience.modeProduct » en el botón.
         Eso ya pasó una vez en el visor de la ficha. */
      boton.textContent = puesta
        ? (boton.dataset.porteOn || 'Produit')
        : (boton.dataset.porteOff || 'Porté');
    }, true);
  }

  const __default = initCardPorte;

  return { initCardPorte, default: __default };
})();

__EDI["components-entry-3d.js"] = (function () {
  'use strict';

  /**
   * Entrada de firma — el interruptor del experimento.
   *
   * Vive detrás de una bandera y por defecto está APAGADO: la Home de siempre
   * es la que se sirve. Comparar dos entradas exige poder volver a la primera
   * sin desplegar nada.
   *
   *   /?entry=3d        el experimento
   *   /?entry=current   la de siempre (o simplemente sin parámetro)
   *
   * Lo único que hace este módulo es decidir si la capa se monta. Toda la
   * composición está en CSS, que es lo que permite que no bloquee nada: el
   * compositor la resuelve sin pasar por JavaScript ni por la GPU en 3D.
   */

  /** ¿Se puede sostener la entrada con lo que hay cargado? */
  function piezaLista(capa, modo) {
    // El estampado es SVG en el documento: no hay nada que descargar ni que
    // esperar, así que esa variante siempre está lista.
    if (modo === 'print') return Boolean(capa.querySelector('.e3d__design'));
    const img = capa.querySelector('.e3d__object');
    // Sin la fotografía de la pieza no hay entrada que enseñar: quedaría el
    // papel en blanco y dos frases flotando, que es peor que no hacer nada.
    return Boolean(img && img.complete && img.naturalWidth > 1);
  }

  function initEntry3D() {
    const capa = document.querySelector('[data-e3d]');
    if (!capa) return;

    /* ── UNA ENTRADA, NO DOS ────────────────────────────────────────────────

       Esta capa fue la entrada de casa mientras la escena de profundidad vivía
       detrás de `?depth=on`. Con la escena ya por defecto son DOS entradas
       seguidas sobre la misma Home, y se ven las dos: a los 47 ms la
       fotografía, a los 425 ms un plano entero de papel con el packshot encima
       tapándola, y a los 1 600 ms la fotografía otra vez, ya con profundidad.
       Un fogonazo blanco en medio de la escena que se acaba de aprobar.

       Así que el packshot vuelve detrás de su bandera. No se ha borrado nada:

         (sin parámetro)  la escena de profundidad — la entrada de la Home
         ?entry=3d        el packshot llegando en profundidad
         ?entry=print     el estampado montándose solo

       Interruptor gemelo del de `DEPTH_POR_DEFECTO` en tools/fragmentos.py, y
       con la misma vuelta atrás: cambiar esta constante a `true`. */
    const PACKSHOT_POR_DEFECTO = false;

    const pedido = new URLSearchParams(location.search).get('entry');
    if (pedido === 'off' || pedido === 'current') return;
    if (!pedido && !PACKSHOT_POR_DEFECTO) return;
    const modo = pedido === 'print' ? 'print' : '3d';

    const quieto = window.matchMedia
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (quieto) return;

    const encender = () => {
      if (!piezaLista(capa, modo)) return;
      document.documentElement.dataset.entryMode = modo;
    };

    if (piezaLista(capa, modo)) encender();
    else {
      /* Se espera a la fotografía, pero con techo. Si tarda, la Home aparece
         sin entrada: ninguna puesta en escena vale una tienda que no se puede
         usar todavía. */
      capa.querySelector('.e3d__object')?.addEventListener('load', encender, { once: true });
      setTimeout(() => { if (!document.documentElement.dataset.entryMode) capa.remove(); }, 900);
    }

    /* La capa se retira del DOM al terminar. Dejarla ahí con `visibility:
       hidden` mantiene un plano de composición a pantalla completa vivo el
       resto de la visita, y eso lo paga el scroll. */
    capa.addEventListener('animationend', (e) => {
      if (e.animationName === 'e3d-salida') capa.remove();
    });

    /* Red de seguridad: `animationend` no llega si la pestaña está en segundo
       plano mientras dura la entrada. Sin esto, la capa se queda —invisible e
       inerte, pero es un plano de composición a pantalla completa vivo el resto
       de la visita, y eso lo paga el scroll—. */
    setTimeout(() => capa.remove(), 2600);
  }

  const __default = initEntry3D;

  return { initEntry3D, default: __default };
})();

__EDI["components-vuelo.js"] = (function () {
  'use strict';

  /**
   * El vuelo entre la rejilla y la ficha.
   *
   * Las transiciones entre documentos las hace el navegador solo, pero necesita
   * saber QUÉ elemento de esta página es el mismo que aquel de la siguiente.
   * Eso se dice con `view-transition-name`, y sólo un elemento por documento
   * puede llevar cada nombre.
   *
   * En una colección hay veinte fotos candidatas y ninguna es « la » foto hasta
   * que alguien pulsa una. Así que el nombre se pone en el momento del clic,
   * sobre la que se acaba de pulsar, y se retira al volver: si se quedara
   * puesto, al pulsar la segunda pieza habría dos elementos con el mismo nombre
   * y el navegador descarta la transición entera.
   *
   * No hay animación escrita aquí. Sólo se le dice al navegador quién es quién.
   */

  const NOMBRE = 'pieza-en-vuelo';

  function initVuelo(raiz = document) {
    if (!('startViewTransition' in document)) return;

    let marcada = null;

    const limpiar = () => {
      if (marcada) marcada.style.viewTransitionName = '';
      marcada = null;
    };

    raiz.addEventListener('click', (e) => {
      /* El botón « Porté » vive dentro del enlace y no navega: si marcara la
         foto, el nombre se quedaría puesto sin que haya transición. */
      if (e.target.closest('[data-card-porte]')) return;

      const enlace = e.target.closest('.card__link');
      if (!enlace) return;

      const card = enlace.closest('.card');
      /* La que se ve AHORA, que no siempre es la primaria: en móvil se puede
         estar mirando la prenda puesta. Volar la otra sería teletransportar. */
      const visible = card?.dataset.showing === 'secondary'
        ? enlace.querySelector('.card__img--secondary')
        : enlace.querySelector('.card__img--primary');
      if (!visible) return;

      limpiar();
      visible.style.viewTransitionName = NOMBRE;
      marcada = visible;
    }, true);

    /* Al volver con el botón atrás la página sale de la caché tal cual la
       dejamos, con el nombre todavía puesto. */
    window.addEventListener('pageswap', limpiar);
    window.addEventListener('pagehide', limpiar);
    window.addEventListener('pageshow', limpiar);
  }

  const __default = initVuelo;

  return { initVuelo, default: __default };
})();

__EDI["adapters-cart-adapter.js"] = (function () {
  'use strict';

  /**
   * CartAdapter — interfaz de carrito.
   *
   * Hoy   → LocalCartAdapter, sobre localStorage.
   * Mañana → ShopifyCartAdapter, sobre /cart/add.js, /cart/change.js y /cart.js.
   *
   * El contrato no cambia al migrar: los mismos métodos, el mismo evento
   * 'edisamp:cart' y la misma forma de línea. La UI escucha el evento y nunca
   * pregunta al carrito por su cuenta.
   *
   * ─── Correspondencia con Shopify ───────────────────────────────────────────
   *   add(line)              → POST /cart/add.js
   *                            { id: variantId, quantity, properties: {...} }
   *   setQuantity(key, n)    → POST /cart/change.js  { id: key, quantity: n }
   *   remove(key)            → POST /cart/change.js  { id: key, quantity: 0 }
   *   read()                 → GET  /cart.js
   *   checkout()             → form POST a /cart con name="checkout",
   *                            o redirección a /checkout
   *
   * La personalización viaja como line item properties: SÓLO identificadores y
   * referencias, nunca el diseño rasterizado.
   *   properties: {
   *     _customization_id: 'EDS-CST-XXXXXXXX',
   *     _preview_url: '…',
   *     _production_ref: '…',
   *     'Design': 'Personnalisé'        // visible para el cliente
   *   }
   * Las claves con guion bajo delante Shopify no las muestra al cliente.
   */

  const STORAGE_KEY = 'edisamp:cart:v2';
  const UNDO_MS = 8000;

  function emit(state, reason) {
    document.dispatchEvent(new CustomEvent('edisamp:cart', { detail: { state, reason } }));
  }

  /** Identidad de la línea: mismo producto + misma variante + mismo diseño. */
  function lineKey(line) {
    return [line.productId, line.variantTitle ?? '', line.customization?.id ?? '']
      .join('|')
      .replace(/\s+/g, '');
  }

  const LocalCartAdapter = {
    read() {
      try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return raw && Array.isArray(raw.lines) ? raw : { lines: [] };
      } catch {
        return { lines: [] };
      }
    },

    write(state, reason = 'update') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        /* modo privado: el carrito vive sólo en memoria durante la sesión */
      }
      emit(state, reason);
      return state;
    },

    /**
     * @param {object} line
     * @param {string} line.productId
     * @param {string} line.handle
     * @param {string} line.name          nombre corto visible
     * @param {string} line.subtitle
     * @param {string} [line.variantGid]  el id que Shopify necesitará
     * @param {string} [line.variantTitle]
     * @param {object} [line.options]     { Couleur, Taille }
     * @param {number} line.price
     * @param {string} [line.image]
     * @param {number} [line.quantity]
     * @param {object} [line.customization] { id, previewUrl, productionRef }
     */
    async add(line) {
      const state = this.read();
      const key = lineKey(line);
      const existing = state.lines.find((l) => l.key === key);
      if (existing) existing.quantity += line.quantity ?? 1;
      else state.lines.push({ key, quantity: line.quantity ?? 1, ...line });
      return this.write(state, 'add');
    },

    async setQuantity(key, quantity) {
      const state = this.read();
      const line = state.lines.find((l) => l.key === key);
      if (!line) return state;
      if (quantity <= 0) return this.remove(key);
      line.quantity = quantity;
      return this.write(state, 'quantity');
    },

    /** Guarda la línea y su posición para poder deshacer. */
    async remove(key) {
      const state = this.read();
      const index = state.lines.findIndex((l) => l.key === key);
      if (index < 0) return state;
      const [removed] = state.lines.splice(index, 1);
      lastRemoved = { line: removed, index, at: Date.now() };
      return this.write(state, 'remove');
    },

    async undoRemove() {
      if (!lastRemoved || Date.now() - lastRemoved.at > UNDO_MS) return null;
      const state = this.read();
      state.lines.splice(Math.min(lastRemoved.index, state.lines.length), 0, lastRemoved.line);
      lastRemoved = null;
      return this.write(state, 'undo');
    },

    hasUndo() {
      return Boolean(lastRemoved && Date.now() - lastRemoved.at <= UNDO_MS);
    },

    async clear() {
      lastRemoved = null;
      return this.write({ lines: [] }, 'clear');
    },

    count() {
      return this.read().lines.reduce((total, line) => total + line.quantity, 0);
    },

    subtotal() {
      return this.read().lines.reduce((total, line) => total + line.price * line.quantity, 0);
    },

    /**
     * En standalone no hay checkout: se registra la intención y se avisa.
     * NO se construye un checkout propio — eso lo hace Shopify.
     */
    async checkout() {
      const state = this.read();
      document.dispatchEvent(new CustomEvent('edisamp:checkout', { detail: state }));
      return { ok: false, reason: 'standalone', lines: state.lines.length };
    },
  };

  let lastRemoved = null;

  const CartAdapter = LocalCartAdapter;
  const MockCart = LocalCartAdapter;

  return { LocalCartAdapter, CartAdapter, MockCart };
})();

__EDI["components-pastilla.js"] = (function () {
  'use strict';
  const { CartAdapter } = __EDI["adapters-cart-adapter.js"];
  const { t } = __EDI["core-i18n.js"];
  /**
   * La pastilla del carrito — lo que confirma que una pieza entró.
   *
   * Antes, añadir abría el panel entero. Cada vez. Eso interrumpe: el cliente
   * estaba mirando una prenda y de golpe tiene el carrito encima tapándola, y
   * lo primero que hace es cerrarlo para volver a lo suyo. Confirmar y
   * secuestrar no son lo mismo.
   *
   * Ahora aparece una pastilla abajo, con la MINIATURA de lo que acaba de
   * añadir y cuántas piezas lleva, y se queda. No tapa nada, no pide nada, y
   * está donde está el pulgar. El panel se abre cuando el cliente quiera —y
   * cuando lo haga, crece DESDE la pastilla, así que el carrito no aparece:
   * es la pastilla, abierta.
   *
   * La miniatura importa más de lo que parece. « 1 artículo » es un número;
   * la foto de la camiseta que acabas de elegir es tu camiseta.
   */


  const NOMBRE_VT = 'panier-pastilla';

  function initPastilla() {
    if (document.querySelector('[data-pastilla]')) return;

    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'pastilla';
    el.dataset.pastilla = '';
    el.hidden = true;
    el.innerHTML =
      '<span class="pastilla__foto" data-pastilla-foto aria-hidden="true"></span>'
      + '<span class="pastilla__texto">'
      + '<span class="pastilla__titulo" data-pastilla-titulo></span>'
      + '<span class="pastilla__cuenta" data-pastilla-cuenta></span></span>'
      + '<span class="pastilla__flecha" aria-hidden="true">&#8594;</span>';
    document.body.append(el);

    const foto = el.querySelector('[data-pastilla-foto]');
    const titulo = el.querySelector('[data-pastilla-titulo]');
    const cuenta = el.querySelector('[data-pastilla-cuenta]');

    function pintar(estado, motivo) {
      const lineas = estado?.lines ?? [];
      const piezas = lineas.reduce((n, l) => n + (l.quantity || 1), 0);

      if (!piezas) {
        el.hidden = true;
        delete el.dataset.entrando;
        return;
      }

      /* La última línea tocada, que es la que el cliente acaba de elegir. */
      const ultima = lineas[lineas.length - 1];
      foto.style.backgroundImage = ultima?.image ? `url("${ultima.image}")` : '';
      foto.dataset.sinFoto = String(!ultima?.image);

      /* `t()` devuelve la CLAVE cuando el diccionario aún no ha llegado —viene
         por fetch—, así que un `||` no la atrapa: 'cart.onePiece' es una cadena
         con valor. Se comprueba que la respuesta no sea la propia clave. Ya
         mordió dos veces en este proyecto. */
      const texto = (clave, respaldo) => {
        const v = t(clave);
        return (!v || v === clave) ? respaldo : v;
      };

      titulo.textContent = texto('cart.title', 'Panier');
      cuenta.textContent = piezas === 1
        ? texto('cart.onePiece', '1 pièce')
        : `${piezas} ${texto('cart.pieces', 'pièces')}`;

      const estabaOculta = el.hidden;
      el.hidden = false;
      el.setAttribute('aria-label', `${titulo.textContent} — ${cuenta.textContent}`);

      /* Sólo se anuncia cuando ENTRA o cuando llega algo nuevo. Rebotar en cada
         repintado —abrir el panel, cambiar cantidad— convierte el acuse de
         recibo en un tic nervioso. */
      if (estabaOculta || motivo === 'add') {
        delete el.dataset.entrando;
        // Lectura forzada: poner y quitar en el mismo fotograma no anima.
        void el.offsetWidth;
        el.dataset.entrando = 'true';
      }
    }

    el.addEventListener('animationend', () => delete el.dataset.entrando);

    el.addEventListener('click', () => {
      /* El panel crece desde aquí. Los dos elementos comparten nombre sólo
         durante la transición: si se quedara puesto, la pastilla y el panel
         serían el mismo elemento para el navegador el resto de la visita. */
      const abrir = () => document.dispatchEvent(new CustomEvent('edisamp:cart:open'));

      if (!document.startViewTransition
          || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        abrir();
        return;
      }

      el.style.viewTransitionName = NOMBRE_VT;
      const vt = document.startViewTransition(abrir);
      vt.finished.finally(() => { el.style.viewTransitionName = ''; });
    });

    document.addEventListener('edisamp:cart', (e) => pintar(e.detail?.state, e.detail?.reason));
    pintar(CartAdapter.read(), 'init');

    return { rect: () => el.getBoundingClientRect(), visible: () => !el.hidden, el };
  }

  const __default = initPastilla;

  return { initPastilla, default: __default };
})();

__EDI["components-cuenta-atras.js"] = (function () {
  'use strict';

  /**
   * La cuenta atrás de una venta flash.
   *
   * REGLA ÚNICA: el reloj que manda es el de quien mira, no el del build.
   *
   * El HTML de este sitio se genera una vez y se queda quieto. Una oferta
   * horneada a las 10:00 sigue diciendo « −20 % » a las 23:00 aunque terminara
   * a mediodía, y ese descuento el carrito no lo hace: el precio lo aplica
   * Shopify, no el HTML. Así que el bloque promocional NACE OCULTO y este
   * módulo es lo único que puede enseñarlo.
   *
   * La inversión es a propósito. Sin JavaScript no se ve la oferta —una venta
   * perdida— en vez de verse una oferta caducada —un precio falso—. De los dos
   * fallos posibles se elige el que no miente.
   *
   * Y cuando el plazo termina con la página abierta, el bloque se retira solo.
   * No hay « 00:00:00 » parpadeando encima de un precio que ya no existe.
   */

  /** Cada cuánto se refresca. Un segundo: ni parpadeo ni reloj de bomba. */
  const LATIDO = 1000;

  /**
   * Formatea lo que queda de la manera más corta que siga siendo exacta.
   *
   * Con días, los segundos sobran y sólo añaden ruido; en la última hora son lo
   * único que importa. Un solo formato para las dos situaciones o miente o
   * distrae.
   */
  function restante(ms, idioma) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sg = s % 60;
    const dd = (n) => String(n).padStart(2, '0');
    if (d > 0) return `${d}${idioma === 'en' ? 'd' : 'j'} ${dd(h)}:${dd(m)}`;
    return `${dd(h)}:${dd(m)}:${dd(sg)}`;
  }

  function leerFecha(el) {
    const v = el.dataset.hasta;
    if (!v) return null;
    const t = Date.parse(v);
    /* Una fecha ilegible NO es « sin plazo »: es un dato roto. Y un dato roto
       sobre el precio se resuelve dejando el bloque oculto. */
    return Number.isNaN(t) ? null : t;
  }

  /** El bloque promocional que contiene este reloj. */
  function bloqueDe(el) {
    return el.closest('[data-promo]') || el.parentElement;
  }

  function initCuentaAtras(raiz = document) {
    const relojes = [...raiz.querySelectorAll('[data-cuenta-atras]')];
    if (!relojes.length) return;

    const idioma = (document.documentElement.lang || 'fr').slice(0, 2);
    const vivos = [];

    for (const el of relojes) {
      const hasta = leerFecha(el);
      const bloque = bloqueDe(el);
      if (!bloque) continue;
      if (hasta === null || Date.now() >= hasta) {
        /* Caducada o rota: se queda como nació. Nadie tiene que hacer nada. */
        bloque.hidden = true;
        continue;
      }
      const salida = el.querySelector('time') || el;
      /* El plazo entero, una vez y en TEXTO REAL, para quien no puede ver el
         reloj correr. Es la información; los dígitos que cambian son la
         decoración.
         Va como texto y no como `aria-label`: un `aria-label` sobre un <p>
         —rol genérico— no se expone de forma fiable, y aquí el <time> que
         hay dentro está `aria-hidden`, así que el párrafo se quedaba mudo. */
      try {
        const f = new Intl.DateTimeFormat(idioma, {
          dateStyle: 'long', timeStyle: 'short',
        }).format(new Date(hasta));
        let leible = el.querySelector('[data-plazo-leible]');
        if (!leible) {
          leible = document.createElement('span');
          leible.className = 'u-visually-hidden';
          leible.setAttribute('data-plazo-leible', '');
          el.prepend(leible);
        }
        leible.textContent = f;
      } catch (e) { /* sin Intl queda el `datetime` del <time>, que es máquina */ }

      bloque.hidden = false;
      vivos.push({ el, salida, bloque, hasta });
    }

    if (!vivos.length) return;

    let id = 0;
    const latir = () => {
      const ahora = Date.now();
      for (let i = vivos.length - 1; i >= 0; i -= 1) {
        const v = vivos[i];
        if (ahora >= v.hasta) {
          /* Se acabó mientras miraba. Fuera el bloque entero, no sólo el
             reloj: lo que caduca es la oferta. */
          v.bloque.hidden = true;
          vivos.splice(i, 1);
          continue;
        }
        v.salida.textContent = restante(v.hasta - ahora, idioma);
      }
      if (!vivos.length) { clearInterval(id); return; }
    };

    latir();
    id = setInterval(latir, LATIDO);

    /* Una pestaña oculta no necesita contar, y al volver hay que ponerse al
       día de golpe: si no, se ve el valor de hace diez minutos. */
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) latir();
    });
  }

  const __default = initCuentaAtras;

  return { initCuentaAtras, default: __default };
})();

__EDI["core-a11y.js"] = (function () {
  'use strict';
  const { $$, trapFocus, lockScroll } = __EDI["core-dom.js"];
  /**
   * Accesibilidad compartida.
   *
   * Aquí vive el patrón de "capa que se abre encima": filtros, guía de tallas y
   * carrito lo usan igual. Escrito una vez y no cuatro, para que no haya tres
   * versiones distintas de atrapar el foco.
   */


  /* ------------------------------------------------------------ ANUNCIOS ---- */

  let region = null;

  function liveRegion() {
    if (region) return region;
    region = document.createElement('div');
    region.className = 'u-visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.append(region);
    return region;
  }

  let writeTimer = null;
  let clearTimer = null;

  /**
   * Anuncia un cambio a los lectores de pantalla.
   *
   * Deliberadamente escueto: «Ajouté au panier» y no el producto, la talla, el
   * color, el subtotal y el número de artículos. Un aria-live que recita medio
   * carrito en cada clic es peor que no tener ninguno.
   *
   * La región se crea SIEMPRE, de forma síncrona, y el texto se escribe en el
   * siguiente tick. Antes esto colgaba de un requestAnimationFrame: si el
   * navegador no pintaba —pestaña en segundo plano, por ejemplo— la región no
   * llegaba a existir y no se anunciaba nada.
   */
  function announce(message) {
    if (!message) return;
    const node = liveRegion();
    clearTimeout(writeTimer);
    clearTimeout(clearTimer);

    if (node.textContent !== message) {
      // Texto distinto: el lector ya detecta el cambio. Se escribe y punto.
      node.textContent = message;
    } else {
      // Mismo texto: hay que vaciar y reescribir, o no se vuelve a anunciar.
      node.textContent = '';
      writeTimer = setTimeout(() => { node.textContent = message; }, 60);
    }

    clearTimer = setTimeout(() => { node.textContent = ''; }, 4000);
  }

  // Se crea al cargar el módulo: una región aria-live que aparece en el DOM
  // justo cuando se anuncia algo puede perderse el primer anuncio.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', liveRegion, { once: true });
  } else {
    liveRegion();
  }

  /* -------------------------------------------------------------- CAPAS ----- */

  const openLayers = [];

  /**
   * Convierte un elemento en una capa modal accesible.
   *
   * Se encarga siempre de lo mismo: foco atrapado dentro, Escape cierra, el foco
   * vuelve a donde estaba, el fondo no hace scroll, y mientras está cerrada la
   * capa es `inert` para que no la recorra un lector de pantalla.
   */
  function createLayer(element, { onOpen, onClose, initialFocus } = {}) {
    if (!element) return null;

    let release = null;
    let lastFocused = null;
    element.setAttribute('inert', '');

    const layer = {
      element,
      get isOpen() {
        return element.dataset.open === 'true';
      },

      open(trigger) {
        if (layer.isOpen) return;
        lastFocused = trigger ?? document.activeElement;
        element.removeAttribute('inert');
        element.dataset.open = 'true';
        lockScroll(true);
        release = trapFocus(element);
        openLayers.push(layer);

        const target =
          (initialFocus && element.querySelector(initialFocus)) ??
          $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', element)[0];
        // `preventScroll`: dar el foco ARRASTRA la página hasta el elemento, y
        // una capa que vive al final del documento —el saludo del −15 % está en
        // el pie— se llevaba la página hasta abajo del todo al abrirse. La capa
        // ya está fija en pantalla: no hay nada que desplazar.
        target?.focus({ preventScroll: true });
        onOpen?.();
      },

      close() {
        if (!layer.isOpen) return;
        element.dataset.open = 'false';
        release?.();
        release = null;
        const index = openLayers.indexOf(layer);
        if (index > -1) openLayers.splice(index, 1);
        if (!openLayers.length) lockScroll(false);

        const back =
          lastFocused && lastFocused !== document.body && document.contains(lastFocused)
            ? lastFocused
            : null;
        element.setAttribute('inert', '');
        back?.focus();
        onClose?.();
      },

      toggle(trigger) {
        layer.isOpen ? layer.close() : layer.open(trigger);
      },
    };

    return layer;
  }

  // Escape cierra siempre la capa de más arriba, nunca todas a la vez.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openLayers.length) return;
    event.stopPropagation();
    openLayers[openLayers.length - 1].close();
  });

  return { announce, createLayer };
})();

__EDI["experience-temporada.js"] = (function () {
  'use strict';

  /**
   * La temporada, pero con manos.
   *
   * QUÉ SEPARA ESTO DEL DECORADO
   * ----------------------------
   * `components/temporada.css` pone las telarañas y la araña: eso es un
   * escaparate, está ahí y no hace nada. Esto es lo otro — lo que RESPONDE a lo
   * que hace el cliente. Un escaparate se mira; una tienda que reacciona se
   * recuerda, y ésa es toda la ventaja que tiene una marca pequeña contra un
   * catálogo que se encuentra más barato en otro sitio.
   *
   * TRES REGLAS
   * -----------
   * 1. NADA SE MUEVE SOLO. Todo lo de aquí arranca porque alguien ha hecho
   *    algo: añadir a la bolsa, buscar. Algo que se mueve por su cuenta
   *    mientras lees es una distracción; algo que contesta a tu dedo es una
   *    conversación.
   * 2. NADA SUSTITUYE INFORMACIÓN. Los murciélagos salen DESPUÉS de que la foto
   *    aterrice, no en su lugar: la foto volando dice « esto exacto ha entrado
   *    en tu bolsa », y eso no se cambia por un adorno.
   * 3. NADA PASA SI NO HAY TEMPORADA. Si el decorado no está en la página, cada
   *    función de aquí devuelve sin hacer nada. No hay un segundo ajuste que
   *    pueda contradecir al primero: la verdad es que exista `.temporada`.
   */

  const REDUCIDO = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  /** ¿Hay temporada? Lo dice el decorado que ya pintó Liquid. */
  const hayTemporada = () => Boolean(document.querySelector('.temporada'));

  const ALAS =
    '<g>'
    + '<path d="M25 7C21 3 15 1.2 8.5 2.2c1.5 1 2.3 2.2 2.5 3.6C8.4 4.6 5.4 4.8 2.4 6.2'
    + 'c2.6 1.2 4.2 3 4.8 5.4.6 2.2.2 4.4-1 6.8 2.4-2.2 4.8-3 7.2-2.4 2.4.6 4.4 2.2 6 4.8'
    + '.8-2.6 2-4.2 3.6-5 .7-.35 1.4-.55 2-.6z"/>'
    + '<g transform="translate(56,0) scale(-1,1)">'
    + '<path d="M25 7C21 3 15 1.2 8.5 2.2c1.5 1 2.3 2.2 2.5 3.6C8.4 4.6 5.4 4.8 2.4 6.2'
    + 'c2.6 1.2 4.2 3 4.8 5.4.6 2.2.2 4.4-1 6.8 2.4-2.2 4.8-3 7.2-2.4 2.4.6 4.4 2.2 6 4.8'
    + '.8-2.6 2-4.2 3.6-5 .7-.35 1.4-.55 2-.6z"/></g>'
    + '<path d="M25.3 6.6 24 1.8l3.2 3zM30.7 6.6 32 1.8l-3.2 3z"/>'
    + '<rect x="25" y="5.6" width="6" height="12" rx="3"/></g>';

  const MURCIELAGO =
    '<path d="M24 3c1.3 0 2.3 1 2.7 2.3C28.6 3.3 31.4 2 34.6 2c3.2 0 5.9 1.3 8 3.7'
    + '-.9-.3-1.7-.2-2.4.3 1.9 1.4 3.2 3.4 3.8 6-1.9-1.2-3.8-1.3-5.7-.3-1.9 1-3.2 2.7-3.9 5.1'
    + '-.8-2-2-3.4-3.6-4.2-1.6-.8-3.5-.7-5.8.4-2.3-1.1-4.2-1.2-5.8-.4-1.6.8-2.8 2.2-3.6 4.2'
    + '-.7-2.4-2-4.1-3.9-5.1-1.9-1-3.8-.9-5.7.3.6-2.6 1.9-4.6 3.8-6-.7-.5-1.5-.6-2.4-.3'
    + '2.1-2.4 4.8-3.7 8-3.7 3.2 0 6 1.3 7.9 3.3C21.7 4 22.7 3 24 3z"/>';

  /** El sello que acompaña al « Ajouté ». Devuelve HTML, no toca el DOM. */
  function selloDeTemporada() {
    if (!hayTemporada()) return '';
    return '<svg class="agregado__sello" viewBox="0 0 40 44" aria-hidden="true" focusable="false">'
      + '<path d="M20 2C10.6 2 3 9.2 3 18.2c0 5.2 2.4 9.4 6 12v4.4c0 1.6 1.3 2.9 2.9 2.9h16.2'
      + 'c1.6 0 2.9-1.3 2.9-2.9v-4.4c3.6-2.6 6-6.8 6-12C37 9.2 29.4 2 20 2z" fill="currentColor"/>'
      + '<ellipse cx="13" cy="19" rx="4.2" ry="5" fill="var(--agregado-fondo, #fff)"/>'
      + '<ellipse cx="27" cy="19" rx="4.2" ry="5" fill="var(--agregado-fondo, #fff)"/>'
      + '<path d="M20 25.5 17.4 30h5.2z" fill="var(--agregado-fondo, #fff)"/>'
      + '<path d="M15 33.5v4.5M20 33.5v4.5M25 33.5v4.5" stroke="var(--agregado-fondo, #fff)" stroke-width="1.7"/>'
      + '</svg>';
  }

  /**
   * Tres murciélagos se espantan de la bolsa cuando la pieza aterriza.
   *
   * Tres y no ocho: lo que se lee de reojo son siluetas separadas, y a partir
   * de cinco es una mancha. Salen hacia ARRIBA y hacia los lados, nunca hacia
   * abajo — hacia abajo taparían la página.
   */
  function murcielagosAlAterrizar(destino) {
    if (!hayTemporada() || REDUCIDO() || !destino) return;
    const caja = destino.getBoundingClientRect();
    if (!caja.width) return;

    /* Recorrido CORTO y a propósito. La primera versión los mandaba 66 px hacia
       arriba y acababan metidos en la barra de anuncios, medio fuera de la
       pantalla y con la opacidad ya cayendo: medido, llegaban al 0,12. Lo que
       se leía era un parpadeo, no tres murciélagos.

       Ahora se quedan alrededor de la bolsa —que es de donde salen— y tardan un
       segundo largo en irse. Lo mismo, pero visible. */
    const salidas = [
      { x: -46, y: -26, giro: -20, ms: 980 },
      { x: 4,   y: -40, giro: 5,   ms: 1120 },
      { x: 44,  y: -22, giro: 22,  ms: 900 },
    ];

    for (const s of salidas) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 48 20');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.innerHTML = MURCIELAGO;
      Object.assign(svg.style, {
        position: 'fixed',
        left: `${caja.left + caja.width / 2 - 11}px`,
        top: `${caja.top + caja.height / 2 - 5}px`,
        width: '26px', height: 'auto',
        fill: 'currentColor', color: 'var(--temporada-tinta, currentColor)',
        zIndex: '9998', pointerEvents: 'none', willChange: 'transform, opacity',
      });
      document.body.append(svg);

      /* La meseta de opacidad es lo que los hace legibles: suben al 0,9 en el
         primer cuarto, SE QUEDAN ahí hasta bien pasada la mitad, y sólo
         entonces se van. Sin meseta, el punto más visible dura un fotograma y
         depende de cuándo aterrice la foto.

         Y la curva va en el PRIMER FOTOGRAMA, no en la animación entera: puesta
         en la animación comprime la línea de tiempo y los `offset` dejan de
         significar lo que dicen — la meseta se cruzaba antes de empezar. Medido
         en el navegador: donde tocaba 0,9 salía 0,22. */
      const anim = svg.animate([
        { transform: 'translate(0,0) scale(.4) rotate(0deg)', opacity: 0,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        { transform: `translate(${(s.x * 0.5).toFixed(1)}px, ${(s.y * 0.55).toFixed(1)}px)`
          + ` scale(1) rotate(${s.giro / 2}deg)`, opacity: 0.9, offset: 0.26 },
        { transform: `translate(${(s.x * 0.82).toFixed(1)}px, ${(s.y * 0.85).toFixed(1)}px)`
          + ` scale(1) rotate(${s.giro}deg)`, opacity: 0.9, offset: 0.62 },
        { transform: `translate(${s.x}px, ${s.y}px) scale(.88) rotate(${s.giro}deg)`, opacity: 0 },
      ], { duration: s.ms, easing: 'linear', fill: 'forwards' });

      const quitar = () => svg.remove();
      anim.finished.then(quitar, quitar);
      // La pestaña oculta detiene los fotogramas y `finished` no llega nunca.
      setTimeout(quitar, s.ms + 400);
    }
  }

  /**
   * La araña que cruza cuando alguien busca.
   *
   * Es la única pieza que se mueve por la pantalla, y sólo lo hace al abrir el
   * buscador: justo cuando la mirada está esperando a que aparezca algo. Una
   * vez por página; repetida a cada tecla sería una mosca, no una araña.
   */
  let yaCruzo = false;
  function araniaCruza() {
    if (!hayTemporada() || REDUCIDO() || yaCruzo) return;
    const capa = document.querySelector('.temporada');
    if (!capa) return;
    yaCruzo = true;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 60 60');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'temporada__corredora');
    svg.innerHTML = '<g class="temporada__patas" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path stroke-width="1.9" d="M25 19 L14 5 M24 22 L10 14 M24 26 L10 31 M25 29 L14 43 M35 19 L46 5 M36 22 L50 14 M36 26 L50 31 M35 29 L46 43"/><path stroke-width="1.25" d="M14 5 L5 9 M10 14 L1 19 M10 31 L2 38 M14 43 L7 54 M46 5 L55 9 M50 14 L59 19 M50 31 L58 38 M46 43 L53 54"/></g><ellipse cx="30" cy="38" rx="9.5" ry="11.5" fill="currentColor"/><path d="M26.5 33 30 36.5 33.5 33M27.5 39.5 30 42 32.5 39.5" fill="none" stroke="#ffffff" stroke-opacity=".18" stroke-width="1.2" stroke-linecap="round"/><ellipse cx="30" cy="23.5" rx="6.2" ry="6.8" fill="currentColor"/><path d="M27.6 18.2 25.6 13.4M32.4 18.2 34.4 13.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/><circle cx="28.3" cy="19.8" r="1.05" fill="var(--temporada-ojo, #ffb703)"/><circle cx="31.7" cy="19.8" r="1.05" fill="var(--temporada-ojo, #ffb703)"/><circle cx="29.1" cy="21.9" r=".7" fill="var(--temporada-ojo, #ffb703)"/><circle cx="30.9" cy="21.9" r=".7" fill="var(--temporada-ojo, #ffb703)"/>';
    capa.append(svg);

    const quitar = () => svg.remove();
    svg.addEventListener('animationend', quitar, { once: true });
    setTimeout(quitar, 3400);
  }

  /**
   * Lo que hay que encender una vez por página.
   *
   * La telaraña se teje SOLA la primera vez de la sesión y luego ya está tejida:
   * verla dibujarse en cada página sería un truco, y un truco repetido catorce
   * veces deja de serlo. El `sessionStorage` puede estar prohibido —ventana
   * privada, cookies bloqueadas— y entonces se teje siempre, que es el fallo
   * bonito de los dos.
   */
  function iniciarTemporada() {
    if (!hayTemporada()) return;
    try {
      if (!sessionStorage.getItem('edisamp:tela')) {
        sessionStorage.setItem('edisamp:tela', '1');
      }
    } catch (e) { /* sin memoria de sesión, se teje cada vez */ }
    iniciarFarol();
  }

  /**
   * La cápsula que vuela al carrito.
   *
   * Fuera de temporada devuelve el clon tal cual y aquí no ha pasado nada.
   * Dentro, envuelve el clon y le pone un murciélago encima, apagado. La foto
   * se apaga y el murciélago se enciende entre el 22 % y el 48 % del vuelo:
   * antes del 22 % lo que vuela sigue siendo el producto —que es cuando hace
   * falta saber qué ha entrado en la bolsa— y a partir del 48 % ya es silueta
   * batiendo las alas.
   *
   * Las dos animaciones son HIJAS del vuelo y no lo tocan: la cápsula se mueve
   * por su lado, esto pasa dentro. Si algo falla, lo que llega al carrito es la
   * foto de siempre.
   */
  function capsulaDeTemporada(clon) {
    if (!hayTemporada() || REDUCIDO()) return clon;

    const capsula = document.createElement('div');
    capsula.setAttribute('aria-hidden', 'true');
    Object.assign(clon.style, {
      width: '100%', height: '100%', display: 'block',
      objectFit: 'cover', margin: '0',
    });
    capsula.append(clon);

    const bicho = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    bicho.setAttribute('viewBox', '0 0 56 24');
    bicho.setAttribute('focusable', 'false');
    bicho.innerHTML = ALAS;
    Object.assign(bicho.style, {
      position: 'absolute', inset: '0', width: '100%', height: '100%',
      fill: 'var(--temporada-tinta, #0b0b0b)', opacity: '0',
      overflow: 'visible',
    });
    capsula.style.position = 'relative';
    capsula.append(bicho);

    const MS = 560;
    clon.animate(
      [{ opacity: 1, offset: 0 }, { opacity: 1, offset: 0.22 },
       { opacity: 0, offset: 0.48 }, { opacity: 0, offset: 1 }],
      { duration: MS, easing: 'linear', fill: 'forwards' });
    bicho.animate(
      [{ opacity: 0, transform: 'scale(.6)', offset: 0 },
       { opacity: 0, transform: 'scale(.8)', offset: 0.22 },
       { opacity: 1, transform: 'scale(1.15)', offset: 0.48 },
       { opacity: 1, transform: 'scale(1)', offset: 1 }],
      { duration: MS, easing: 'linear', fill: 'forwards' });

    /* El aleteo: las alas se encogen y se estiran a lo ancho. Es lo que hace un
       murciélago visto de frente, y cuesta una animación en vez de dos alas
       articuladas que a 30 px no se distinguirían. */
    bicho.animate(
      [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0.66)' }, { transform: 'scaleX(1)' }],
      { duration: 220, iterations: Math.ceil(MS / 220), easing: 'ease-in-out',
        composite: 'add' });

    return capsula;
  }

  /**
   * El farol: se aplasta y suelta dos murciélagos.
   *
   * Es lo único del decorado que responde al dedo, así que tiene que responder
   * BIEN: se hunde con peso, la vela da un fogonazo y salen dos bichos. Y se
   * puede volver a pulsar en cuanto termina — un juguete que hay que esperar
   * para volver a usarlo deja de ser un juguete.
   */
  function iniciarFarol() {
    const farol = document.querySelector('[data-temporada-calabaza]');
    if (!farol) return;
    farol.addEventListener('click', () => {
      if (farol.dataset.aplastada === 'true') return;
      farol.dataset.aplastada = 'true';
      murcielagosAlAterrizar(farol);
      const soltar = () => delete farol.dataset.aplastada;
      farol.addEventListener('animationend', soltar, { once: true });
      setTimeout(soltar, 900);
    });
  }

  return { selloDeTemporada, murcielagosAlAterrizar, araniaCruza, iniciarTemporada, capsulaDeTemporada, iniciarFarol };
})();

__EDI["components-busqueda.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  const { createLayer } = __EDI["core-a11y.js"];
  const { t, locale } = __EDI["core-i18n.js"];
  const { araniaCruza } = __EDI["experience-temporada.js"];
  /**
   * Búsqueda del catálogo.
   *
   * Existía la lupa y no existía la búsqueda: un `<button aria-label="Rechercher">`
   * en la cabecera de TODAS las páginas, sin destino y sin manejador. Un control
   * que no hace nada es peor que no tenerlo — el cliente lo pulsa, no pasa nada,
   * y a partir de ahí desconfía del resto de la interfaz.
   *
   * Por eso el botón nace `hidden` en el HTML y es este módulo el que lo enseña.
   * Si el módulo no carga, no hay lupa. La misma regla que la escena de
   * profundidad y que la venta flash: se prefiere la ausencia a la promesa
   * incumplida.
   *
   * DE DÓNDE SALEN LOS RESULTADOS
   * -----------------------------
   * Del buscador de Shopify: `/search/suggest.json`. Antes salían de
   * `ProductRepository`, que pide `/data/products.json` — una instantánea del
   * prototipo que en una tienda de verdad NO EXISTE. Resultado: la lupa se
   * abría, se escribía « tasse », y no aparecía nada. Nunca. Comprobado en la
   * tienda escribiendo bien la palabra y recibiendo cero.
   *
   * Shopify busca sobre las 140 piezas reales, en el servidor, y entiende
   * palabras a medias. Lo que no hace es perdonar erratas, así que eso se pone
   * aquí: si no encuentra nada, se reintenta con la palabra más larga recortada.
   * Quien escribe « tassse » se come la última letra de más, y « tass » la
   * encuentra igual.
   */


  /** Lo que se tarda en dejar de teclear. Por debajo se filtra en cada letra. */
  const ESPERA = 140;

  /** Sin acentos y en minúsculas: « pièce » y « piece » son la misma búsqueda. */
  function plano(s) {
    return (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  /**
   * Distancia de edición entre dos palabras, ACOTADA.
   *
   * Sale en cuanto sabe que va a pasar de `tope`, que es lo que la hace barata:
   * no interesa saber si dos palabras están a distancia 9, sólo si están a 1.
   */
  function distancia(a, b, tope) {
    if (Math.abs(a.length - b.length) > tope) return tope + 1;
    let fila = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      let anterior = fila[0];
      fila[0] = i;
      let minimo = fila[0];
      for (let j = 1; j <= b.length; j++) {
        const previo = fila[j];
        fila[j] = a[i - 1] === b[j - 1]
          ? anterior
          : Math.min(anterior, fila[j], fila[j - 1]) + 1;
        anterior = previo;
        if (fila[j] < minimo) minimo = fila[j];
      }
      if (minimo > tope) return tope + 1;
    }
    return fila[b.length];
  }

  /**
   * Cuántas letras se le perdonan a una palabra.
   *
   * Nada por debajo de cuatro: con tres letras, perdonar una convierte « tee »
   * en « the » y en « the », y la búsqueda deja de significar nada. De cuatro a
   * seis, una. De siete en adelante, dos — « personalise » y « personnalisé »
   * son la misma intención escrita por alguien con prisa.
   */
  function tolerancia(palabra) {
    if (palabra.length < 4) return 0;
    if (palabra.length < 7) return 1;
    return 2;
  }

  /**
   * Cuánto encaja un producto con lo buscado.
   *
   * Devuelve 0 si alguna palabra no aparece en ningún campo: buscar « boxy
   * femme » tiene que dar las boxy de mujer, no todo lo boxy MÁS todo lo de
   * mujer. Un OR aquí convierte la búsqueda en un listado.
   */
  function puntua(p, palabras) {
    const nombre = plano(p.name);
    const campos = [
      [nombre, 6],
      [plano(p.subtitle), 3],
      [plano(p.type), 2],
      [plano(p.universe), 2],
      [plano(p.handle).replace(/-/g, ' '), 1],
      [plano((p.variants || []).map((v) => Object.values(v.options || {}).join(' ')).join(' ')), 1],
    ];
    let total = 0;
    for (const palabra of palabras) {
      let mejor = 0;
      for (const [texto, peso] of campos) {
        if (!texto.includes(palabra)) continue;
        /* Empezar por la palabra vale más que contenerla: quien escribe « ang »
           busca ANGEL, no « drowning in thoughts ». */
        mejor = Math.max(mejor, texto.startsWith(palabra) ? peso * 2 : peso);
      }
      /* Nada exacto. Antes de rendirse, se prueba con erratas: quien escribe
         « camisseta » o « tshrit » está buscando lo mismo que quien lo escribe
         bien, y devolverle CERO resultados es perder la venta por una tecla.
         Vale la MITAD que un acierto exacto, así que lo escrito bien siempre
         sale primero. */
      if (!mejor) {
        const tope = tolerancia(palabra);
        if (!tope) return 0;
        for (const [texto, peso] of campos) {
          for (const suelta of texto.split(/[^a-z0-9]+/)) {
            if (suelta.length < 3) continue;
            if (distancia(palabra, suelta, tope) <= tope) {
              mejor = Math.max(mejor, peso / 2);
              break;
            }
          }
        }
      }

      if (!mejor) return 0;
      total += mejor;
    }
    return total;
  }

  /** El precio llega como « 15.90 », no en céntimos. */
  function dineroDe(texto, moneda) {
    const n = Number(texto);
    if (!Number.isFinite(n)) return '';
    try {
      return new Intl.NumberFormat(locale() || 'fr', {
        style: 'currency', currency: moneda,
      }).format(n);
    } catch {
      return n.toFixed(2) + ' ' + moneda;
    }
  }

  /**
   * Le pregunta a Shopify. Devuelve [] ante cualquier fallo: una lupa que no
   * encuentra es mala, una que rompe la página es peor.
   */
  async function preguntar(q, señal) {
    const url = '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product&resources[limit]=8'
      + '&resources[options][unavailable_products]=last';
    try {
      const r = await fetch(url, { signal: señal, headers: { Accept: 'application/json' } });
      if (!r.ok) return [];
      const d = await r.json();
      return d?.resources?.results?.products || [];
    } catch {
      return [];
    }
  }

  /**
   * Lo mismo, pero perdonando una errata.
   *
   * Shopify no corrige faltas. Lo que sí sabe es buscar por principio de
   * palabra, así que cuando no encuentra nada se reintenta con la palabra más
   * larga RECORTADA: « tassse » no da nada, « tass » da las tazas. Cubre la
   * errata más común, que es la del final —la mano va más rápida que la vista—
   * y no inventa resultados donde no los hay: si el recorte tampoco encuentra,
   * se dice que no hay.
   */
  async function preguntarConIndulgencia(q, señal) {
    const directo = await preguntar(q, señal);
    if (directo.length) return directo;

    const palabras = q.split(/\s+/).filter(Boolean);
    const larga = palabras.slice().sort((a, b) => b.length - a.length)[0] || '';
    const tope = tolerancia(plano(larga));
    if (!tope) return [];

    const recorte = larga.slice(0, Math.max(3, larga.length - tope));
    if (recorte === larga) return [];
    const otra = palabras.map((w) => (w === larga ? recorte : w)).join(' ');
    return preguntar(otra, señal);
  }

  function initBusqueda() {
    const panel = $('[data-buscar]');
    const abrir = $('[data-buscar-abrir]');
    if (!panel || !abrir) return;

    const capa = createLayer(panel, { initialFocus: '[data-buscar-input]' });
    const input = $('[data-buscar-input]', panel);
    const lista = $('[data-buscar-lista]', panel);
    const vacio = $('[data-buscar-vacio]', panel);
    const vacioTitulo = $('[data-buscar-vacio-titulo]', panel);
    const recuento = $('[data-buscar-recuento]', panel);
    const limpiar = $('[data-buscar-limpiar]', panel);
    const pista = $('[data-buscar-pista]', panel) || $('#buscar-pista');

    const moneda = document.body.dataset.moneda || 'CHF';
    let enCurso = null;

    /* La lupa sólo aparece cuando hay panel y hay módulo. */
    abrir.hidden = false;
    abrir.addEventListener('click', () => {
      capa.open(abrir);
      /* Una araña cruza justo cuando la mirada está esperando resultados. Una
         vez por página; a cada tecla sería una mosca. Fuera de temporada no
         hace nada. */
      araniaCruza();
    });
    $('[data-buscar-cerrar]', panel)?.addEventListener('click', () => capa.close());

    /** Un <span> con clase y texto. `textContent`, nunca HTML. */
    function trozo(clase, texto) {
      const s = document.createElement('span');
      s.className = clase;
      if (texto != null) s.textContent = texto;
      return s;
    }

    /**
     * Una fila de resultado, construida nodo a nodo.
     *
     * La primera versión usaba una plantilla con `innerHTML` e interpolaba el
     * nombre, el subtítulo, el handle y la URL de la imagen. Es cómodo de leer
     * y es un agujero: cualquiera de esos cuatro campos que traiga `<` se
     * convierte en marcado, y en un tema que se VENDE el catálogo es de otro.
     * Que el catálogo venga de Shopify no lo arregla — un título de producto
     * acepta lo que le escriban.
     *
     * Escapar habría bastado, pero construir el DOM quita el vector entero en
     * vez de taparlo: aquí no hay ningún sitio donde el texto pueda volverse
     * etiqueta.
     */
    function fila(p) {
      const li = document.createElement('li');
      li.className = 'buscar__fila';

      const a = document.createElement('a');
      a.className = 'buscar__enlace';
      /* `handle` no se interpola en una cadena de HTML: se asigna como
         propiedad, y el navegador se encarga de codificarlo. */
      /* La URL viene de Shopify con sus propios parámetros de analítica; se usa
         tal cual para que la tienda sepa qué búsqueda llevó a qué producto. */
      a.setAttribute('href', p.url || ('/products/' + encodeURIComponent(p.handle) + '/'));

      const mini = trozo('buscar__miniatura', null);
      /* Shopify manda la foto en `image` (y en `featured_image.url`). Se le pide
         pequeña: en una fila de 56 px no hace falta traerse 1600. */
      const bruta = p.image || p.featured_image?.url || '';
      const url = bruta ? bruta.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';
      if (url) {
        const img = document.createElement('img');
        img.src = url;                 // `src` como propiedad: no puede salirse
        img.alt = '';
        img.width = 56; img.height = 70;
        img.loading = 'lazy'; img.decoding = 'async';
        mini.append(img);
      }

      const texto = trozo('buscar__texto', null);
      texto.append(trozo('buscar__nombre', p.title || ''),
                   trozo('buscar__sub t-small t-muted', p.type || ''));

      a.append(mini, texto, trozo('buscar__precio', dineroDe(p.price, moneda)));
      li.append(a);
      return li;
    }

    async function pintar() {
      const q = (input.value || '').trim();
      limpiar.hidden = !q;

      if (!q) {
        /* Vacío es vacío. Una lista de « populares » que nadie ha medido sería
           una recomendación inventada. */
        lista.replaceChildren();
        vacio.hidden = true;
        if (pista) pista.hidden = false;
        recuento.textContent = '';
        return;
      }

      /* Una tecla más cancela la consulta anterior: si no, la respuesta lenta de
         « tas » puede llegar después de la de « tasse » y pintar lo viejo. */
      enCurso?.abort();
      enCurso = new AbortController();
      const mia = enCurso;

      const encontrados = await preguntarConIndulgencia(q, mia.signal);
      if (mia !== enCurso) return;                   // llegó tarde: se descarta
      if ((input.value || '').trim() !== q) return;  // ya se escribe otra cosa

      if (pista) pista.hidden = true;
      lista.replaceChildren(...encontrados.map(fila));
      vacio.hidden = encontrados.length > 0;
      if (!encontrados.length) {
        vacioTitulo.textContent = t('search.noResults', { q });
      }
      recuento.textContent = t('search.results', { count: encontrados.length });
    }

    let temporizador = 0;
    input.addEventListener('input', () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(pintar, ESPERA);
    });

    limpiar.addEventListener('click', () => {
      input.value = '';
      pintar();
      input.focus();
    });

    /* Abajo y arriba recorren los resultados sin sacar la mano del teclado; el
       `input` conserva el foco visual, que es donde se sigue escribiendo. */
    input.addEventListener('keydown', (ev) => {
      if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
      const enlaces = $$('.buscar__enlace', lista);
      if (!enlaces.length) return;
      ev.preventDefault();
      enlaces[ev.key === 'ArrowDown' ? 0 : enlaces.length - 1].focus();
    });

    lista.addEventListener('keydown', (ev) => {
      const enlaces = $$('.buscar__enlace', lista);
      const i = enlaces.indexOf(document.activeElement);
      if (i < 0) return;
      if (ev.key === 'ArrowDown') { ev.preventDefault(); (enlaces[i + 1] || input).focus(); }
      if (ev.key === 'ArrowUp') { ev.preventDefault(); (enlaces[i - 1] || input).focus(); }
    });
  }

  const __default = initBusqueda;

  return { initBusqueda, default: __default };
})();

__EDI["experience-pointer.js"] = (function () {
  'use strict';

  /**
   * Paralaje de puntero: el producto acusa el ratón, nada más.
   *
   * Seis píxeles y un 1,2 % de escala. Suena a poco y esa es la intención: el
   * gesto tiene que leerse como que la foto responde, no como que se mueve.
   * En cuanto se nota el movimiento, deja de parecer un producto caro y
   * empieza a parecer un carrusel.
   *
   * Sólo con ratón. En una pantalla táctil no hay hover que responder, y
   * escuchar punteros gruesos sólo sirve para que la imagen dé un salto justo
   * cuando el dedo va a tocarla.
   */

  const FINO = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const QUIETO = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * @param {HTMLElement} el  contenedor; recibe --xp-px / --xp-py en −1..1
   * @returns {() => void}    para desmontar
   */
  function attachPointer(el) {
    if (!el || !FINO() || QUIETO()) return () => {};

    // El testigo se levanta ANTES de pedir el frame y se baja dentro de él.
    // Guardar el id que devuelve requestAnimationFrame parece equivalente,
    // pero deja el testigo en alto si el frame llega a correr antes de la
    // asignación: entonces nadie vuelve a pedir uno y el efecto muere.
    let pendiente = false;
    let id = 0;
    let px = 0;
    let py = 0;

    const pintar = () => {
      pendiente = false;
      // Mientras se arrastra manda el arrastre. Sumar los dos movimientos
      // convierte un gesto claro en una imagen que se resiste.
      if (el.dataset.xpDragging === 'true') return;
      el.style.setProperty('--xp-px', px.toFixed(3));
      el.style.setProperty('--xp-py', py.toFixed(3));
    };

    const mover = (e) => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      px = ((e.clientX - r.left) / r.width) * 2 - 1;
      py = ((e.clientY - r.top) / r.height) * 2 - 1;
      if (pendiente) return;
      pendiente = true;
      id = requestAnimationFrame(pintar);
    };

    const entrar = () => { el.dataset.xpPointer = 'true'; };

    const salir = () => {
      if (pendiente) { cancelAnimationFrame(id); pendiente = false; }
      px = 0;
      py = 0;
      // Se vuelve al centro con el atributo aún puesto: así la transición
      // lleva la imagen a su sitio en vez de cortarla a mitad de camino.
      el.style.setProperty('--xp-px', '0');
      el.style.setProperty('--xp-py', '0');
      delete el.dataset.xpPointer;
    };

    el.addEventListener('pointerenter', entrar);
    el.addEventListener('pointermove', mover);
    el.addEventListener('pointerleave', salir);

    return () => {
      if (pendiente) cancelAnimationFrame(id);
      el.removeEventListener('pointerenter', entrar);
      el.removeEventListener('pointermove', mover);
      el.removeEventListener('pointerleave', salir);
      salir();
    };
  }

  /** Engancha todo lo marcado con .xp-parallax dentro de una raíz. */
  function attachAllPointers(raiz = document) {
    const sueltos = [...raiz.querySelectorAll('.xp-parallax')].map(attachPointer);
    return () => sueltos.forEach((f) => f());
  }

  return { attachPointer, attachAllPointers };
})();

__EDI["components-pieza-404.js"] = (function () {
  'use strict';
  const { $ } = __EDI["core-dom.js"];
  const { attachPointer } = __EDI["experience-pointer.js"];
  /**
   * La pieza que no existe todavía.
   *
   * El 404 ya tenía una buena idea: el marco discontinuo es la zona de
   * impresión del Studio, y aquí significa que no hay nada que imprimir. Esto
   * la lleva un paso más: el marco se inclina con el puntero y sus tres planos
   * —la retícula, el número y el propio marco— se separan de verdad, cada uno a
   * su distancia.
   *
   * ARQUITECTURA, LA MISMA QUE COSTÓ UNA FASE EN EL HERO
   * ----------------------------------------------------
   * Este módulo NO escribe ni un transform. Publica `--xp-px` / `--xp-py` en el
   * contenedor y ya está; quién los consume y sobre qué nodo lo decide el CSS.
   * Un nodo, un dueño. Mezclar los dos fue exactamente lo que hundió la primera
   * versión de la escena de profundidad.
   *
   * `attachPointer` se encarga de lo demás: no hace nada si no hay puntero fino
   * —en un teléfono no se sobrevuela— ni si se ha pedido menos movimiento. En
   * los dos casos queda la composición quieta, que es una composición, no una
   * versión pobre de otra cosa.
   */


  function initPieza404() {
    const pieza = $('[data-pieza-404]');
    if (!pieza) return;

    /* El testigo lo pone JS: sin él, el CSS deja la pieza plana. Así una página
       con el módulo caído no enseña un marco a medio inclinar. */
    pieza.dataset.viva = 'true';
    return attachPointer(pieza);
  }

  const __default = initPieza404;

  return { initPieza404, default: __default };
})();

__EDI["core-wishlist.js"] = (function () {
  'use strict';

  /**
   * Favoris — la lista de deseos del cliente.
   *
   * HACE FALTA CUENTA PARA GUARDAR. No es una barrera por gusto: un favorito
   * que vive en `localStorage` es de ESTE navegador y de nadie más, así que se
   * pierde al cambiar de teléfono y —esto es lo que importa— no se le puede
   * avisar a nadie de que su pieza bajó de precio. Franklin: « sus favoritos
   * tienen que guardarse siempre… para mandarle notificación de que su favorito
   * está disponible o está en promoción ». Eso empieza por saber de quién es la
   * lista.
   *
   * El almacén sigue siendo el navegador —un tema de Shopify no puede escribir
   * en la cuenta del cliente sin una app, y eso está explicado en
   * `components/wishlist-cuenta.js`—, pero la llave lleva el identificador del
   * cliente: dos cuentas en el mismo ordenador no se mezclan, y el día que haya
   * app proxy la lista que se sube ya es de alguien.
   *
   * Se guardan identificadores y lo mínimo para pintar una tarjeta sin volver
   * a pedir el catálogo. Nada de precios: el precio se lee del catálogo al
   * mostrar, porque un precio guardado envejece y miente.
   */

  const BASE = 'edisamp:wishlist:v1';

  /** El identificador del cliente, o '' si no ha entrado. Lo pone el layout. */
  const clienteId = () =>
    (document.documentElement.dataset.cliente || '').trim();

  const haySesion = () => clienteId() !== '';

  /** Una llave por cliente. Sin sesión no hay llave: no se escribe nada. */
  const llave = () => {
    const id = clienteId();
    return id ? `${BASE}:${id}` : null;
  };

  const leer = () => {
    const k = llave();
    if (!k) return [];
    try {
      const raw = localStorage.getItem(k);
      const lista = raw ? JSON.parse(raw) : [];
      return Array.isArray(lista) ? lista : [];
    } catch { return []; }
  };

  const escribir = (lista) => {
    const k = llave();
    if (!k) return false;
    try {
      localStorage.setItem(k, JSON.stringify(lista));
      emitir(lista);
      return true;
    } catch { return false; }
  };

  /**
   * Lo que se guardó antes de pedir cuenta no se tira.
   *
   * Hasta hoy la lista vivía en `edisamp:wishlist:v1`, sin dueño. Quien tenía
   * piezas apartadas y ahora entra se las encuentra en su cuenta: se funden una
   * sola vez y el cajón viejo se vacía, para que el siguiente que entre en este
   * mismo ordenador no herede los favoritos de otro.
   */
  function heredarLoViejo() {
    const k = llave();
    if (!k) return;
    let viejo;
    try { viejo = localStorage.getItem(BASE); } catch { return; }
    if (!viejo) return;
    try {
      const sueltos = JSON.parse(viejo);
      if (Array.isArray(sueltos) && sueltos.length) {
        const mios = leer();
        const vistos = new Set(mios.map((x) => x.handle));
        const fundida = [...mios];
        for (const x of sueltos) {
          if (x && x.handle && !vistos.has(x.handle)) { vistos.add(x.handle); fundida.push(x); }
        }
        escribir(fundida);
      }
      localStorage.removeItem(BASE);
    } catch { /* si el cajón viejo está corrupto, se ignora y se sigue */ }
  }

  const oyentes = new Set();
  const onChange = (fn) => { oyentes.add(fn); return () => oyentes.delete(fn); };
  const emitir = (lista) => oyentes.forEach((fn) => fn(lista));

  const all = () => leer();
  const count = () => leer().length;
  const has = (handle) => leer().some((x) => x.handle === handle);

  function add({ handle, title, image = null, url = null }) {
    if (!handle) return false;
    const lista = leer();
    if (lista.some((x) => x.handle === handle)) return true;
    lista.push({ handle, title, image, url, addedAt: new Date().toISOString() });
    return escribir(lista);
  }

  const remove = (handle) => escribir(leer().filter((x) => x.handle !== handle));

  function toggle(item) {
    const dentro = has(item.handle);
    if (dentro) remove(item.handle); else add(item);
    return !dentro;
  }

  const clear = () => escribir([]);

  /**
   * Sustituye la lista entera de una vez.
   *
   * Existe para la fusión con la cuenta: al entrar desde otro dispositivo hay
   * que escribir la lista fundida, y hacerlo con `clear()` + `add()` en bucle
   * emitiría un evento por pieza — la interfaz se redibujaría veinte veces y
   * cualquier oyente contaría veinte cambios donde hubo uno.
   */
  const replace = (lista) =>
    escribir(Array.isArray(lista) ? lista.filter((x) => x && x.handle) : []);

  /**
   * Conecta los botones de favorito de la página.
   *
   * Se delega en el documento: las tarjetas se crean y se destruyen al filtrar,
   * y un listener por tarjeta se quedaría huérfano.
   */
  function mount(root = document) {
    const sincronizar = () => {
      for (const b of root.querySelectorAll('[data-wishlist-toggle]')) {
        const dentro = has(b.dataset.wishlistToggle);
        b.setAttribute('aria-pressed', String(dentro));
        const etiqueta = dentro ? 'Retirer des favoris' : 'Ajouter aux favoris';
        b.setAttribute('aria-label', etiqueta);
        b.setAttribute('title', etiqueta);
      }
      for (const n of root.querySelectorAll('[data-wishlist-count]')) {
        const c = count();
        n.textContent = c ? String(c) : '';
        n.hidden = c === 0;
      }
    };

    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-wishlist-toggle]');
      if (!b) return;
      e.preventDefault();

      /* Sin cuenta no se guarda nada — y se dice por qué, ahí mismo. El aviso
         lo pinta `components/favoritos-puerta.js`: aquí sólo se avisa de que
         alguien quiso guardar algo, porque el almacén no tiene que saber de
         interfaz. */
      if (!haySesion()) {
        document.dispatchEvent(new CustomEvent('edisamp:favoritos-sin-cuenta', {
          detail: { handle: b.dataset.wishlistToggle, boton: b },
        }));
        return;
      }

      toggle({
        handle: b.dataset.wishlistToggle,
        title: b.dataset.wishlistTitle || b.dataset.wishlistToggle,
        image: b.dataset.wishlistImage || null,
        url: b.dataset.wishlistUrl || null,
      });
      sincronizar();
    });

    heredarLoViejo();
    onChange(sincronizar);
    sincronizar();
    return sincronizar;
  }

  return { clienteId, haySesion, onChange, all, count, has, add, remove, toggle, clear, replace, mount };
})();

__EDI["components-wishlist-cuenta.js"] = (function () {
  'use strict';
  const { $ } = __EDI["core-dom.js"];
  const wishlist = __EDI["core-wishlist.js"];
  /**
   * Los favoritos, cuando hay cuenta.
   *
   * LO QUE UN TEMA PUEDE Y NO PUEDE HACER
   * -------------------------------------
   * Esto hay que decirlo antes que nada, porque es la pregunta que hace
   * cualquiera que compre el tema: **un tema de Shopify NO puede escribir en
   * la cuenta del cliente por su cuenta.** No hay API desde el escaparate para
   * guardar un metafield del cliente. Quien diga lo contrario está vendiendo
   * algo que no funciona.
   *
   * Lo que sí se puede, y es lo que hace este módulo:
   *
   *   LEER    `customer.metafields.<ns>.wishlist` si existe. Lo rellena una
   *           app del comerciante —propia, con un app proxy, o una del
   *           mercado—. Si no hay app, no hay metafield y no pasa nada.
   *
   *   FUNDIR  lo del navegador con lo de la cuenta. Al entrar por primera vez
   *           en otro dispositivo, lo que había aquí NO se pierde: se suma.
   *
   *   ENVIAR  la lista fundida a un app proxy, si el comerciante configuró uno.
   *           Sin proxy no se intenta y no se miente: la lista sigue siendo de
   *           este navegador y la página lo dice.
   *
   * De los dos fallos posibles —perder favoritos o prometer una sincronización
   * que no existe— se elige el que no miente.
   */


  /** Dónde escribe el comerciante, si tiene app. Sale del <html>. */
  function rutaDeSync() {
    const r = document.documentElement.dataset.wishlistProxy;
    /* Sólo rutas del propio dominio. Un proxy de Shopify vive en `/apps/…`, y
       aceptar una URL absoluta desde el DOM sería mandar la lista de deseos de
       un cliente a donde diga el marcado. */
    return r && r.startsWith('/') && !r.startsWith('//') ? r : null;
  }

  /** Lo que la cuenta ya tenía, puesto por el generador desde el metafield. */
  function deLaCuenta() {
    const nodo = $('[data-wishlist-cuenta]');
    const crudo = nodo && nodo.dataset.wishlistInicial;
    if (!crudo) return null;
    try {
      const lista = JSON.parse(crudo);
      return Array.isArray(lista) ? lista : null;
    } catch { return null; }
  }

  /** Une sin duplicar, conservando el orden en que se añadieron. */
  function fundir(a, b) {
    const vistos = new Set();
    const salida = [];
    for (const item of [...(a || []), ...(b || [])]) {
      const id = item && (item.handle || item.id);
      if (!id || vistos.has(id)) continue;
      vistos.add(id);
      salida.push(item);
    }
    return salida;
  }

  async function initWishlistCuenta() {
    const seccion = $('[data-wishlist-cuenta]');
    if (!seccion) return;                       // no estamos en la cuenta

    const cuenta = deLaCuenta();
    const local = wishlist.all();

    /* Sin metafield no hay nada que fundir: la lista es de este navegador y la
       sección tiene que decirlo en vez de dejar que se descubra al cambiar de
       teléfono. */
    if (cuenta === null) {
      seccion.dataset.sincronizada = 'no';
      return;
    }

    const fundida = fundir(cuenta, local);
    seccion.dataset.sincronizada = 'si';

    /* Lo que venga de la cuenta manda en este navegador desde ya, aunque el
       envío de vuelta falle: leer siempre funciona, escribir depende de que el
       comerciante tenga app. */
    wishlist.replace(fundida);

    const ruta = rutaDeSync();
    if (!ruta || fundida.length === local.length) return;

    try {
      await fetch(ruta, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* `same-origin`: la sesión del cliente no sale del dominio de la
           tienda ni por accidente. */
        credentials: 'same-origin',
        body: JSON.stringify({ wishlist: fundida }),
      });
    } catch {
      /* Silencio a propósito. Que la subida falle no es un error que el cliente
         tenga que leer: sus favoritos siguen aquí. */
    }
  }

  const __default = initWishlistCuenta;

  return { initWishlistCuenta, default: __default };
})();

__EDI["core-muelle.js"] = (function () {
  'use strict';

  /**
   * Muelles. La física con la que se mueve todo lo que se toca.
   *
   * POR QUÉ UN MUELLE Y NO UNA TRANSICIÓN
   * -------------------------------------
   * Una transición CSS tiene duración fija y una curva escrita de antemano: no
   * puede responder a lo que pase MIENTRAS corre. Si alguien vuelve a pulsar a
   * mitad de camino, la animación anterior se corta de golpe y se ve el salto.
   *
   * Un muelle no tiene duración: tiene un destino. Cambiar el destino a mitad de
   * vuelo es lo normal para él, y arranca desde donde esté AHORA, con la
   * velocidad que lleve. Por eso todo lo que el cliente puede tocar dos veces
   * seguidas —un corazón, un botón, un cajón— se mueve con esto y no con una
   * transición.
   *
   * LOS DOS PARÁMETROS
   * ------------------
   * No mass/stiffness/damping, que no significan nada al mirarlos, sino los dos
   * que usa Apple y que sí se pueden imaginar:
   *
   *   amortiguacion  1.0 = llega y se para, sin rebote. Por debajo, rebota.
   *                  Se baja SÓLO cuando el gesto traía impulso: un rebote en
   *                  algo que apareció solo se siente mal; en algo que acabas de
   *                  lanzar, bien.
   *
   *   respuesta      cuánto tarda en llegar, en segundos. NO es una duración —
   *                  un muelle no tiene—, es lo rápido que reacciona.
   *
   * Integra con Euler semi-implícito a paso fijo. El paso real de pantalla se
   * trocea en pasos pequeños: con un fotograma largo —una pestaña que vuelve del
   * fondo— la integración explota y el elemento sale disparado.
   */

  const PASO = 1 / 240;        // segundos por paso de integración
  const PASO_MAXIMO = 0.064;   // ~4 fotogramas. Más que eso, se descarta
  const QUIETO = 0.0005;       // por debajo de esto ya no se ve mover

  /**
   * Un muelle sobre un número.
   *
   * @param {object} opciones
   * @param {number} opciones.desde          valor inicial
   * @param {number} opciones.amortiguacion  1 = sin rebote
   * @param {number} opciones.respuesta      segundos
   * @param {(v:number)=>void} opciones.pinta  qué hacer con el valor
   * @param {()=>void} [opciones.alParar]
   */
  function muelle({ desde = 0, amortiguacion = 1, respuesta = 0.4, pinta, alParar }) {
    let valor = desde;
    let destino = desde;
    let velocidad = 0;
    let corriendo = false;
    let ultimo = 0;
    let cuadro = 0;

    /* ω: la frecuencia natural. De ahí salen las dos únicas fuerzas que hay
       aquí — la que tira hacia el destino y la que frena.

       No son constantes: se pueden cambiar en pleno vuelo. Es lo que permite
       que un botón se hunda con un muelle seco al pulsarlo y vuelva con uno
       alegre al soltarlo, SIN cortar el movimiento — el valor y la velocidad
       siguen siendo los mismos, sólo cambia la ley que los gobierna. */
    let z = amortiguacion;
    let w = (2 * Math.PI) / respuesta;

    function paso(dt) {
      const fuerza = -(w * w) * (valor - destino);
      const freno = -2 * z * w * velocidad;
      velocidad += (fuerza + freno) * dt;
      valor += velocidad * dt;
    }

    function marco(ahora) {
      cuadro = 0;
      let dt = (ahora - ultimo) / 1000;
      ultimo = ahora;
      /* Un fotograma larguísimo no se integra: se ignora. Es lo que pasa al
         volver a una pestaña que estaba en segundo plano, y sin esta guarda el
         elemento aparece en la otra punta de la pantalla. */
      if (!(dt > 0)) dt = PASO;
      if (dt > PASO_MAXIMO) dt = PASO_MAXIMO;

      let restante = dt;
      while (restante > 0) {
        paso(Math.min(PASO, restante));
        restante -= PASO;
      }

      pinta(valor);

      if (Math.abs(valor - destino) < QUIETO && Math.abs(velocidad) < QUIETO) {
        valor = destino;
        velocidad = 0;
        pinta(valor);
        corriendo = false;
        if (alParar) alParar();
        return;
      }
      cuadro = requestAnimationFrame(marco);
    }

    function arrancar() {
      if (corriendo) return;
      corriendo = true;
      ultimo = performance.now();
      cuadro = requestAnimationFrame(marco);
    }

    return {
      /**
       * Nuevo destino. Se puede cambiar en pleno vuelo: eso es lo que hace.
       *
       * El segundo argumento admite un número —un impulso— o un objeto para
       * REAFINAR el muelle sin cortarlo: `{ impulso, amortiguacion, respuesta }`.
       */
      a(nuevo, opciones) {
        destino = nuevo;
        if (typeof opciones === 'number') {
          velocidad = opciones;
        } else if (opciones) {
          if (typeof opciones.amortiguacion === 'number') z = opciones.amortiguacion;
          if (typeof opciones.respuesta === 'number') w = (2 * Math.PI) / opciones.respuesta;
          if (typeof opciones.impulso === 'number') velocidad = opciones.impulso;
        }
        arrancar();
      },
      /** Poner el valor sin animar — para reducir movimiento, o al montar. */
      ya(nuevo) {
        cancelAnimationFrame(cuadro);
        corriendo = false;
        valor = nuevo;
        destino = nuevo;
        velocidad = 0;
        pinta(valor);
      },
      parar() {
        cancelAnimationFrame(cuadro);
        corriendo = false;
      },
      get valor() { return valor; },
      get velocidad() { return velocidad; },
      get activo() { return corriendo; },
    };
  }

  /**
   * Los muelles de la casa. Un vocabulario corto y usado siempre igual: si cada
   * componente inventa el suyo, el sitio se mueve de nueve maneras distintas y
   * ninguna se reconoce como suya.
   */
  const MUELLES = {
    /* Lo normal: llega y se para. Nada que celebrar. */
    firme: { amortiguacion: 1, respuesta: 0.35 },
    /* Se pulsa y responde al instante. Corto a propósito. */
    pulsacion: { amortiguacion: 1, respuesta: 0.16 },
    /* Con rebote, y SÓLO donde hay algo que celebrar — un favorito, un añadido.
       El rebote es la diferencia entre « hecho » y « ¡hecho! », y por eso no
       puede estar en todas partes. */
    alegre: { amortiguacion: 0.55, respuesta: 0.34 },
    /* Un cajón que se arrastra: lleva impulso, así que puede pasarse un poco. */
    cajon: { amortiguacion: 0.8, respuesta: 0.3 },
  };

  /** ¿Quiere movimiento quien mira? Se pregunta cada vez: se cambia en caliente. */
  function hayMovimiento() {
    return typeof matchMedia === 'function'
      ? !matchMedia('(prefers-reduced-motion: reduce)').matches
      : true;
  }

  return { muelle, MUELLES, hayMovimiento };
})();

__EDI["components-favorito.js"] = (function () {
  'use strict';
  const { $$ } = __EDI["core-dom.js"];
  const { muelle, MUELLES, hayMovimiento } = __EDI["core-muelle.js"];
  /**
   * El corazón.
   *
   * Guardar una pieza es el único gesto de toda la tienda que no compra nada y
   * que aun así se hace por gusto. Es el sitio donde un poco de alegría está
   * justificada — y casi el único.
   *
   * QUÉ PASA, EN ORDEN
   * ------------------
   *   se pulsa      →  el botón se hunde AL INSTANTE. No al soltar: en cuanto
   *                    el dedo toca. Esperar al `click` para dar señal es lo que
   *                    hace que una interfaz se sienta muerta.
   *   se suelta y   →  el corazón se llena de rojo, sale disparado por encima
   *   queda guardado   de su tamaño y vuelve; un anillo se abre y se apaga; un
   *                    brillo cruza la silueta. Y una vibración corta, una sola.
   *   se quita      →  el rojo se vacía y el corazón vuelve a su sitio. Sin
   *                    anillo, sin brillo, sin vibración: quitar algo no se
   *                    celebra.
   *
   * POR QUÉ CON MUELLE Y NO CON UNA TRANSICIÓN
   * ------------------------------------------
   * Porque a un corazón se le da doble clic. Con una transición CSS, el segundo
   * clic corta el primero a media curva y se ve el salto. El muelle arranca
   * desde donde esté el corazón AHORA y con la velocidad que lleve, así que
   * pulsar cinco veces seguidas se siente como cinco empujones al mismo objeto
   * y no como cinco animaciones peleándose.
   *
   * Lo que NO hace: guardar. De eso se ocupa `core/wishlist.js`, que ya estaba y
   * es la autoridad. Esto sólo mira el resultado y lo cuenta.
   */


  /* Cuánto se hunde al pulsar y cuánto se pasa al guardar. Medido a ojo en
     pantalla: por debajo de 1.25 no se lee como celebración y por encima de 1.4
     parece un fallo. */
  const HUNDIDO = 0.88;
  /* 13 y no 9: con 9 el corazón llegaba a 1.20 —medido en el navegador— y 1.20
     no se lee como celebración, se lee como un temblor. Con 13 llega a 1.31
     —medido también—, que es donde deja de parecer un fallo y empieza a
     parecer un sí. */
  const IMPULSO_ALEGRIA = 13;

  const muelles = new WeakMap();

  function muelleDe(boton) {
    let m = muelles.get(boton);
    if (!m) {
      m = muelle({
        desde: 1,
        ...MUELLES.pulsacion,
        pinta: (v) => { boton.style.setProperty('--fav-escala', v.toFixed(4)); },
      });
      muelles.set(boton, m);
    }
    return m;
  }

  /** Dispara una animación decorativa y la retira sola. */
  function destello(boton, nombre) {
    boton.dataset.destello = nombre;
    const limpiar = () => {
      delete boton.dataset.destello;
      boton.removeEventListener('animationend', limpiar);
    };
    boton.addEventListener('animationend', limpiar);
    /* Si el navegador no dispara `animationend` —pestaña en segundo plano, por
       ejemplo— el atributo se queda pegado y el siguiente clic no reinicia nada.
       Un plazo de seguridad, generoso, que no se nota si llega el evento. */
    setTimeout(limpiar, 1200);
  }

  function initFavorito() {
    const botones = $$('[data-wishlist-toggle]');
    if (!botones.length) return;

    /* Delegado en el documento: las tarjetas aparecen y desaparecen —una
       colección que pagina, un panel de búsqueda— y enganchar botón a botón
       deja sin corazón a todo lo que llegue después. */

    document.addEventListener('pointerdown', (e) => {
      const b = e.target.closest('[data-wishlist-toggle]');
      if (!b) return;
      if (!hayMovimiento()) return;
      muelleDe(b).a(HUNDIDO, MUELLES.pulsacion);
    });

    /* Soltar fuera del botón, o cancelar el gesto, también tiene que devolverlo
       a su sitio: si no, queda hundido para siempre. */
    for (const evento of ['pointerup', 'pointercancel', 'pointerleave']) {
      document.addEventListener(evento, (e) => {
        const b = e.target.closest('[data-wishlist-toggle]');
        if (!b) return;
        if (!hayMovimiento()) return;
        /* En `pointerup` sobre el botón no se devuelve aquí: lo hace el `click`,
           que sabe si quedó guardado o no. Esto es sólo para los finales que no
           terminan en clic. */
        if (evento === 'pointerup') return;
        muelleDe(b).a(1);
      }, true);
    }

    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-wishlist-toggle]');
      if (!b) return;

      /* El estado lo escribe `core/wishlist.js` en su propio manejador. Se lee
         en el siguiente fotograma para no depender del orden en que se
         registraron los dos. */
      requestAnimationFrame(() => {
        const guardado = b.getAttribute('aria-pressed') === 'true';

        if (!hayMovimiento()) {
          muelleDe(b).ya(1);
          return;
        }

        const m = muelleDe(b);
        if (guardado) {
          /* Se le da un empujón hacia arriba y se deja que el muelle alegre haga
             el resto: sube por encima de 1, vuelve, y se para. El rebote sale de
             la física, no de una curva dibujada a mano. */
          m.a(1, { ...MUELLES.alegre, impulso: IMPULSO_ALEGRIA });
          destello(b, 'guardado');
          /* Una vibración corta y UNA sola. En cada toque sería un tic nervioso;
             aquí confirma algo que el dedo acaba de hacer. */
          if (navigator.vibrate) navigator.vibrate(10);
        } else {
          /* Al quitar vuelve con el muelle seco: sin rebote, sin fiesta. */
          m.a(1, MUELLES.firme);
        }
      });
    });

    /* El muelle vive en `--fav-escala`, así que un corazón que nace guardado
       —al recargar— tiene que arrancar con el valor puesto o salta al primer
       toque. */
    for (const b of botones) muelleDe(b).ya(1);
  }

  return { initFavorito };
})();

__EDI["components-favoritos-puerta.js"] = (function () {
  'use strict';
  const { t } = __EDI["core-i18n.js"];
  /**
   * La puerta de los favoritos.
   *
   * Alguien toca un corazón sin haber entrado. Lo que NO se hace: guardar en
   * silencio en este navegador y dejar que lo descubra dentro de un mes, con el
   * teléfono nuevo en la mano y la lista vacía. Lo que se hace: decirlo en el
   * momento, en una frase, y dejar la puerta abierta al lado.
   *
   * Es un aviso, no un muro: no atrapa el foco, no oscurece la página y no
   * bloquea nada. Se puede seguir comprando sin cuenta — lo único que pide
   * cuenta es GUARDAR, porque un favorito sin dueño no se le puede avisar a
   * nadie cuando la pieza entra en promoción.
   *
   * Vuelve a la misma página después de entrar: `return_url` con la ruta de
   * ahora. Quien estaba mirando un jersey vuelve al jersey, no a la portada.
   */


  const CLASE = 'favpuerta';
  let aviso = null;
  let cerrarSolo = null;

  /**
   * Sólo el propio dominio: el enlace lo pone el layout, pero se mira igual.
   *
   * Con las cuentas nuevas de Shopify esto no es « /account/login » sino una
   * URL absoluta a la propia tienda, así que vale una ruta relativa o una
   * absoluta cuyo origen sea este. Cualquier otra cosa se descarta: mandar a un
   * cliente a « iniciar sesión » en otro dominio es exactamente el aspecto que
   * tiene un phishing.
   */
  function ruta(nombre, porDefecto) {
    const r = document.documentElement.dataset[nombre];
    if (!r) return porDefecto;
    if (r.startsWith('/') && !r.startsWith('//')) return r;
    try {
      if (new URL(r, location.origin).origin === location.origin) return r;
    } catch { /* si no es una URL, se cae al valor por defecto */ }
    return porDefecto;
  }

  /** Adonde volver después de entrar: aquí mismo. */
  function conVuelta(base) {
    const vuelta = location.pathname + location.search;
    /* La ruta de Shopify puede traer ya su propia query. Con « ? » a secas
       salía una dirección con dos interrogaciones y la vuelta se perdía. */
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}return_url=${encodeURIComponent(vuelta)}`;
  }

  function construir() {
    const el = document.createElement('div');
    el.className = CLASE;
    el.dataset.favpuerta = '';
    el.setAttribute('role', 'status');
    el.hidden = true;

    const entrar = conVuelta(ruta('cuentaEntrar', '/account/login'));
    const crear = conVuelta(ruta('cuentaCrear', '/account/register'));

    el.innerHTML =
      `<p class="${CLASE}__texto">${t('wishlist.gate_text')}</p>`
      + `<div class="${CLASE}__acciones">`
      + `<a class="btn btn--solid ${CLASE}__entrar" href="${entrar}">`
      + `<span>${t('wishlist.gate_login')}</span></a>`
      + `<a class="${CLASE}__crear" href="${crear}">${t('wishlist.gate_register')}</a>`
      + '</div>'
      + `<button type="button" class="${CLASE}__cerrar" data-favpuerta-cerrar`
      + ` aria-label="${t('a11y.close')}"></button>`;

    el.querySelector('[data-favpuerta-cerrar]').addEventListener('click', ocultar);
    document.body.append(el);
    return el;
  }

  function ocultar() {
    if (!aviso) return;
    delete aviso.dataset.abierto;
    clearTimeout(cerrarSolo);
    /* Se espera a que termine la salida antes de esconderlo del todo: si se
       pone `hidden` a la vez, el aviso desaparece de golpe. */
    setTimeout(() => { if (aviso && !aviso.dataset.abierto) aviso.hidden = true; }, 320);
  }

  function mostrar() {
    if (!aviso) aviso = construir();
    aviso.hidden = false;
    /* Un fotograma entre enseñarlo y marcarlo abierto, o la transición de
       entrada no llega a correr: el navegador nunca vio el estado de partida. */
    requestAnimationFrame(() => { aviso.dataset.abierto = ''; });
    clearTimeout(cerrarSolo);
    cerrarSolo = setTimeout(ocultar, 9000);
  }

  function initFavoritosPuerta() {
    document.addEventListener('edisamp:favoritos-sin-cuenta', mostrar);
  }

  const __default = initFavoritosPuerta;

  return { initFavoritosPuerta, default: __default };
})();

__EDI["components-decorado-reposo.js"] = (function () {
  'use strict';

  /**
   * El decorado sólo para cuando NADIE puede verlo.
   *
   * Aquí hubo una versión que pausaba la escena de Halloween en cuanto salía de
   * la pantalla. Ahorraba de verdad, pero Franklin la paró en seco: « yo quiero
   * que esté siempre corriendo ». Y tiene razón en lo que le importa — la
   * bruja, los murciélagos y el coche fúnebre son la tienda, no un adorno que
   * se apaga para ganar milisegundos.
   *
   * Así que la escena corre siempre mientras la pestaña esté delante. Lo único
   * que se sigue parando es lo que NADIE está mirando por definición: cuando el
   * cliente se va a otra pestaña o bloquea el teléfono. Eso no es quitar nada
   * —no hay ojos al otro lado— y son minutos de batería en un móvil que sigue
   * teniendo la tienda abierta en segundo plano.
   *
   * La velocidad se ganó por otro lado, que era donde estaba de verdad: el
   * trabajo de pintado de las secciones que quedan fuera de la pantalla
   * (`content-visibility`, en `base.css`) y los difuminados de las tarjetas.
   * Ninguna de las dos cosas se ve.
   *
   * POR QUÉ LA CLASE VA EN EL <html> Y NO EN CADA ESCENA
   * ---------------------------------------------------
   * Porque la regla de CSS que pausa vive detrás de `.js-reposo-todo`. Si este
   * módulo no llega a cargarse, la clase no se pone, la regla no aplica y el
   * decorado se comporta como siempre. El fallo posible es « no ahorra », nunca
   * « se quedó congelado ».
   */

  function initDecoradoReposo() {
    const alCambiar = () => {
      document.documentElement.classList.toggle('js-reposo-todo', document.hidden);
    };
    document.addEventListener('visibilitychange', alCambiar);
    alCambiar();
  }

  const __default = initDecoradoReposo;

  return { initDecoradoReposo, default: __default };
})();

__EDI["components-anuncios.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  const { hayMovimiento } = __EDI["core-muelle.js"];
  /**
   * La barra de anuncios, cuando hay más de uno.
   *
   * Uno solo se lee. Tres seguidos en la misma línea no los lee nadie, así que
   * se turnan.
   *
   * LO QUE NO HACE:
   *
   *   · no se mueve si quien mira ha pedido menos movimiento. Se queda el
   *     primero, que es el que el comerciante puso primero;
   *   · no gira mientras el ratón está encima ni mientras el foco está dentro:
   *     un enlace que desaparece bajo el cursor es un enlace que no se puede
   *     pulsar;
   *   · no gira con la pestaña en segundo plano. Volver y encontrarse el cuarto
   *     mensaje sin haber visto los tres anteriores no informa de nada.
   *
   * Y es un fundido de opacidad, no un desplazamiento: la barra mide 34 px y
   * cualquier movimiento vertical ahí dentro se lee como un parpadeo.
   */


  function initAnuncios() {
    const barra = $('[data-anuncios]');
    if (!barra) return;

    const mensajes = $$('.header__anuncio', barra);
    if (mensajes.length < 2) return;

    const cada = Number(barra.dataset.cada) * 1000;
    if (!(cada > 0)) return;
    if (!hayMovimiento()) return;

    let i = 0;
    let reloj = 0;
    let parado = false;

    function siguiente() {
      mensajes[i].hidden = true;
      i = (i + 1) % mensajes.length;
      mensajes[i].hidden = false;
      /* La animación de entrada vive en el CSS y se dispara al dejar de estar
         oculto: aquí no se toca ni una propiedad de estilo. */
    }

    function arrancar() {
      if (reloj || parado) return;
      reloj = setInterval(() => {
        if (document.hidden) return;
        siguiente();
      }, cada);
    }

    function detener() {
      clearInterval(reloj);
      reloj = 0;
    }

    barra.addEventListener('pointerenter', () => { parado = true; detener(); });
    barra.addEventListener('pointerleave', () => { parado = false; arrancar(); });
    barra.addEventListener('focusin', () => { parado = true; detener(); });
    barra.addEventListener('focusout', () => { parado = false; arrancar(); });

    arrancar();
  }

  return { initAnuncios };
})();

__EDI["components-escena.js"] = (function () {
  'use strict';

  /**
   * La escena de profundidad de la Home — arranque.
   *
   * REGLA ÚNICA: PEDIDA no es LISTA.
   *
   * `?depth=on` sólo anota que el visitante la ha pedido. La Home normal se
   * pinta igual que siempre y se queda debajo. Esta función es lo único que
   * puede declarar la escena LISTA, y sólo lo hace cuando se cumple TODO:
   *
   *   1. las dos fotografías han descargado Y decodificado —`decode()` promete
   *      píxeles, no bytes; `complete` no distingue las dos cosas—;
   *   2. el runtime del hero ha arrancado, así que el puntero y los universos
   *      funcionan y la escena no llega a una página a medio montar.
   *
   * Si algo de eso falla —una imagen que no llega, `hero.js` que no carga,
   * este módulo que ni se ejecuta— no pasa absolutamente nada: se queda la
   * fotografía de siempre. No hay ningún camino hacia el hero en negro, que es
   * lo que la versión anterior sí tenía: escondía `.hero__media` en el primer
   * fotograma y luego esperaba.
   *
   * Por la misma razón aquí NO hay plazo máximo. Un techo servía para escapar
   * de una pantalla vacía; sin pantalla vacía, esperar es gratis y enseñar una
   * escena a medio decodificar no lo es.
   *
   * No toca ningún transform. La propiedad del movimiento está repartida en
   * `hero-depth.css` y este archivo no participa.
   */

  /**
   * La fotografía de siempre, ya descargada.
   * -----------------------------------------------------------------------------
   * Medido en 4G con caché fría: arrancando las capas a la vez que ella, la
   * fotografía base terminaba a los 3 922 ms; sin ellas, a los 2 684 ms. Aunque
   * vayan en `fetchpriority="low"`, comparten el ancho de banda con lo único que
   * el cliente necesita ver. La mejora espera su turno: es una mejora.
   */
  function baseDescargada() {
    const base = document.querySelector('.hero__media img');
    /* `complete` a secas, sin mirar `naturalWidth`: lo que se espera aquí es
       que la base DEJE DE OCUPAR LA RED, y una descarga fallida ya no ocupa.
       Con la comprobación de ancho, una base rota daba `complete: true` y
       `naturalWidth: 0`, no entraba por aquí, y se quedaba esperando un
       `load`/`error` que había ocurrido antes de escuchar: la escena no
       arrancaba nunca. */
    if (!base || base.complete) return Promise.resolve();
    return new Promise((listo) => {
      base.addEventListener('load', listo, { once: true });
      base.addEventListener('error', listo, { once: true });
    });
  }

  /**
   * La página, pintando de verdad.
   *
   * `document.hidden` NO sirve para esto: una ventana tapada por otra sigue
   * declarándose visible en macOS y aun así deja de pintar. `requestAnimationFrame`
   * es el único que dice la verdad — no llega mientras no hay fotogramas y llega
   * en cuanto los hay, sin que haya que escuchar nada.
   */
  function pintando() {
    return new Promise((listo) => requestAnimationFrame(() => listo()));
  }

  /** El runtime del hero, listo. Puede haber ocurrido ya. */
  function heroArrancado() {
    const hero = document.querySelector('[data-hero]');
    if (!hero) return Promise.reject(new Error('sin hero'));
    if (hero.dataset.heroListo === 'true') return Promise.resolve();
    return new Promise((listo) => {
      document.addEventListener('edisamp:hero-listo', () => listo(), { once: true });
    });
  }

  function initEscena() {
    const raiz = document.documentElement;
    if (raiz.dataset.depthRequested !== 'on') return;

    const escena = document.querySelector('[data-escena]');
    if (!escena) return;

    /* Una sola cadena de arranque. Hoy `app.js` llama a esto una vez, pero la
       escena ya es el comportamiento normal de la Home y `?depth=on` sigue
       aceptándose por compatibilidad: una segunda llamada volvería a asignar
       las fuentes y a pedir otro `decode()` de lo mismo. Que no pueda pasar
       sale más barato que confiar en que no pase. */
    if (escena.dataset.arrancada === 'true') return;
    escena.dataset.arrancada = 'true';

    /* Las fuentes viven en `data-src` para que la Home normal no pague por
       ellas: un <img> con `src` se descarga aunque su contenedor esté en
       `display: none`. Eran 835 KB que nadie llegaba a ver. */
    const fotos = [...escena.querySelectorAll('img[data-src]')];
    if (!fotos.length) return;

    /* El orden importa más que la prioridad. Las capas no empiezan hasta que la
       fotografía base está en casa: `fetchpriority="low"` reparte el ancho de
       banda, no lo reserva.

       Y el `decode()` se pide cuando la página esté pintando. Medido: se
       resuelve DENTRO del pipeline de pintado, así que una ventana que no pinta
       deja la promesa pendiente. */
    const cargadas = baseDescargada().then(() => {
      for (const img of fotos) {
        /* Las fuentes alternativas primero: un `<source>` que llegue DESPUÉS
           del `src` ya no participa en la elección. El navegador escoge por
           soporte de formato —AVIF si puede, si no la de siempre— y nunca se
           descarga la que descarta. */
        const pic = img.parentElement;
        if (pic && pic.tagName === 'PICTURE') {
          for (const s of pic.querySelectorAll('source[data-srcset]')) s.srcset = s.dataset.srcset;
        }
        img.src = img.dataset.src;
      }
      return pintando();
    });

    const decodificadas = cargadas.then(() => Promise.all(fotos.map(
      (img) => (img.decode ? img.decode() : Promise.resolve()))));

    Promise.all([decodificadas, heroArrancado()]).then(() => {
      /* Antes de promocionar, se le pregunta a la página si el titular clásico
         llegó a verse. No es un plazo ni una suposición: es leer lo que hay en
         pantalla. Con caché caliente la escena está lista a los ~50 ms, cuando
         la entrada del texto todavía no lo ha encendido (retardo 200 ms), y
         hacerle una transición sería encenderlo para apagarlo. */
      const titular = document.querySelector('.hero__headline');
      if (titular) {
        const visible = parseFloat(getComputedStyle(titular).opacity) > 0.01;
        raiz.dataset.titularCede = visible ? 'suave' : 'seco';
      }
      raiz.dataset.depthReady = 'on';
      /* La entrada va en el fotograma siguiente al descubrimiento: encenderla a
         la vez arriesga a que el primer keyframe se pinte antes que la escena. */
      requestAnimationFrame(() => { escena.dataset.entrada = 'on'; });
    }).catch(() => {
      /* Silencio a propósito. Una mejora que no llega no es un error que
         reportar al cliente: es una Home normal, que es lo que venía a usar. */
    });
  }

  const __default = initEscena;

  return { initEscena, default: __default };
})();

__EDI["components-header.js"] = (function () {
  'use strict';
  const { $, $$, trapFocus, lockScroll, prefersReducedMotion } = __EDI["core-dom.js"];
  const { CartAdapter } = __EDI["adapters-cart-adapter.js"];
  const { t } = __EDI["core-i18n.js"];
  /**
   * Header — tres estados independientes, ninguno acoplado al otro:
   *   data-over-hero  el hero todavía está debajo → transparente, tinta clara
   *   data-scrolled   ya no estamos arriba del todo → papel translúcido
   *   data-compact    el usuario baja → barra más baja; sube → barra completa
   *
   * El markup ya viene en el HTML. Aquí solo se le añade comportamiento.
   */


  const COMPACT_AFTER = 120;   // px antes de permitir compactar

  /* Cuánto hay que moverse EN LA MISMA DIRECCIÓN para que el header cambie de
     forma. Antes bastaban 6 px de un solo evento, y un trackpad manda decenas
     de eventos diminutos que se contradicen: la barra oscilaba entre alta y
     compacta mientras el dedo seguía quieto. Ahora se acumula el recorrido y
     sólo cuenta cuando pasa el umbral.

     Asimétrico a propósito: esconder cuesta más que volver. Bajar es una
     intención sostenida; subir es casi siempre «devuélveme la navegación». */
  const RECORRIDO_PARA_COMPACTAR = 56;
  const RECORRIDO_PARA_VOLVER = 18;

  function initHeader() {
    const header = $('.header');
    if (!header) return;

    const superficie = setupSurface(header);
    setupScrollStates(header, superficie);
    setupMobileMenu(header);
    setupBagCount(header);
  }

  /* --------------------------------------------------- superficie y tinta ---

     Un solo sitio decide las dos cosas. La explicación larga está en
     header.css; la corta es que «encima del hero» y «con scroll» eran dos
     estados independientes que podían ser ciertos a la vez, y esa combinación
     pintaba tinta blanca sobre papel blanco: 1.05:1. */

  function setupSurface(header) {
    const hero = $('[data-hero]');
    /* Sin hero declarado la página es papel, así que tinta oscura. El defecto
       NO puede ser 'paper' —que es tinta clara— porque una página sin hero es
       justo donde no hay nada oscuro debajo que la sostenga. */
    const esquemaHero = hero?.dataset.headerScheme ?? 'ink';

    let sobreHero = Boolean(hero);
    let conScroll = false;

    function pintar() {
      const superficie = conScroll ? 'paper' : 'transparent';
      header.dataset.surface = superficie;
      header.dataset.ink = superficie === 'paper' ? 'ink'
        : (sobreHero ? esquemaHero : 'ink');
      // Se conservan para depurar y para los tests; ya no pintan nada.
      header.dataset.overHero = String(sobreHero);
      header.dataset.scrolled = String(conScroll);
    }

    if (hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sobreHero = entry.isIntersecting;
        pintar();
      }, { rootMargin: `-${header.offsetHeight}px 0px 0px 0px`, threshold: 0 })
        .observe(hero);
    }

    pintar();
    return {
      set scrolled(v) { conScroll = v; pintar(); },
      get scrolled() { return conScroll; },
    };
  }

  /* ------------------------------------------------------- estados de scroll */

  function setupScrollStates(header, superficie) {
    let ultimaY = window.scrollY;
    let recorrido = 0;      // acumulado sin cambiar de sentido
    let direccion = 0;      // 1 bajando, -1 subiendo
    let pendiente = false;

    function update() {
      const y = window.scrollY;
      const delta = y - ultimaY;
      ultimaY = y;

      if (superficie.scrolled !== (y > 8)) superficie.scrolled = y > 8;

      if (delta !== 0) {
        const d = Math.sign(delta);
        // Cambiar de sentido reinicia: cuenta el recorrido hecho sin cambiar
        // de idea, no la suma de los temblores.
        if (d !== direccion) { direccion = d; recorrido = 0; }
        recorrido += Math.abs(delta);

        const umbral = d > 0 ? RECORRIDO_PARA_COMPACTAR : RECORRIDO_PARA_VOLVER;
        if (recorrido >= umbral) {
          header.dataset.compact = String(d > 0 && y > COMPACT_AFTER);
        }
      }

      // Arriba del todo no hay nada que recoger.
      if (y <= COMPACT_AFTER) header.dataset.compact = 'false';
      pendiente = false;
    }

    function onScroll() {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --------------------------------------------------------- menú fullscreen */

  function setupMobileMenu(header) {
    const menu = $('#site-menu');
    const openBtn = $('.header__burger', header);
    const closeBtn = $('.menu__close', menu ?? document);
    if (!menu || !openBtn || !closeBtn) return;

    let releaseFocus = null;
    let lastFocused = null;

    /* Entrada escalonada: 40 ms por entrada, nunca más de ~300 ms en total.
       Va por GRUPO y no por enlace: desde que cada colección lleva debajo sus
       pastillas de subcategoría, contar enlaces sueltos metía a la última
       pastilla de Accessoires medio segundo de retraso — el menú terminaba de
       aparecer por partes. El grupo entra entero, con su colección y lo suyo. */
    $$('.menu__grupo', menu).forEach((grupo, index) => {
      grupo.style.setProperty('--stagger', prefersReducedMotion() ? '0ms' : `${60 + index * 40}ms`);
    });

    function open() {
      lastFocused = document.activeElement;
      menu.removeAttribute('inert');
      menu.dataset.open = 'true';
      openBtn.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      releaseFocus = trapFocus(menu);
      closeBtn.focus();
    }

    function close() {
      menu.dataset.open = 'false';
      openBtn.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      releaseFocus?.();
      releaseFocus = null;

      // Primero inerte (el foco sale del menú), luego se coloca a donde debe ir.
      // Si quien abrió ya no es enfocable, el foco iría a parar al <body>:
      // el botón del menú es siempre un destino válido.
      const back =
        lastFocused && lastFocused !== document.body && document.contains(lastFocused)
          ? lastFocused
          : openBtn;
      menu.setAttribute('inert', '');
      back.focus();
    }

    openBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);

    // Navegar cierra el menú; Escape también.
    $$('a', menu).forEach((link) => link.addEventListener('click', close));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.dataset.open === 'true') close();
    });

    // Al pasar a desktop el menú deja de tener sentido: se cierra solo.
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (event) => {
      if (event.matches && menu.dataset.open === 'true') close();
    });
  }

  /* ----------------------------------------------------- contador del panier */

  function setupBagCount(header) {
    const badge = $('.header__badge span', header);
    const action = $('.header__action--bag', header);

    function render() {
      const count = CartAdapter.count();
      header.dataset.bagCount = String(count);
      if (badge) badge.textContent = count > 9 ? '9+' : String(count);
      if (action) action.setAttribute('aria-label', t('a11y.bagCount', { count }));
    }

    render();
    document.addEventListener('edisamp:cart', render);
  }

  return { initHeader };
})();

__EDI["components-nav-surface.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  /**
   * Navegación editorial.
   *
   * No es un mega-menú: no hay caja blanca con cuatro columnas y veinte
   * enlaces. Es una superficie que se descuelga de la cabecera y enseña lo que
   * hay de verdad detrás de cada entrada — la colección, tres piezas reales con
   * su fotografía, y en CUSTOM la pieza en blanco y los tres pasos.
   *
   * Tres decisiones que la separan de un dropdown de tienda:
   *
   *   1. Pasar de HOMME a FEMME **no cierra y reabre**. La superficie se queda
   *      y cambia por dentro. Cerrar y abrir es lo que hace que un menú se
   *      sienta como un menú; quedarse es lo que hace que se sienta como una
   *      página que responde.
   *   2. Abrir y cerrar tienen INTENCIÓN. Cruzar un enlace de camino a otro
   *      sitio no abre nada, y salir un instante hacia el panel no lo cierra.
   *      Sin eso, la superficie parpadea con cada movimiento del ratón.
   *   3. Las fotografías del panel se piden al ACERCARSE, no al cargar la
   *      página. Un menú que descarga doce imágenes que nadie va a mirar es
   *      exactamente el peso que este sitio no quiere.
   *
   * El teclado no es una alternativa peor: enfocar abre, ↓ entra en el panel,
   * Escape cierra y devuelve el foco. Y el enlace sigue siendo un enlace: quien
   * pulsa Enter va a la colección, que es lo que dice que hace.
   */


  /* Intención, en milisegundos. No son duraciones de animación —eso vive en
     tokens.css— sino cuánto hay que insistir para que algo cuente como una
     decisión. Abrir pide poco; cerrar pide más, porque el viaje del enlace al
     panel pasa por un hueco. */
  const ABRIR = 110;
  const CERRAR = 220;

  function initNavSurface() {
    const superficie = $('[data-nav-surface]');
    const scrim = $('[data-nav-scrim]');
    const disparadores = $$('[data-nav-trigger]');
    if (!superficie || !disparadores.length) return;

    const paneles = new Map(
      $$('[data-nav-panel]', superficie).map((p) => [p.dataset.navPanel, p])
    );

    let abierto = null;
    let temporizador = 0;
    const cancelar = () => { clearTimeout(temporizador); temporizador = 0; };

    /* Las imágenes del panel nacen perezosas. Al acercarse a su entrada se
       piden: para cuando la superficie termine de bajar, ya están. */
    const precargados = new Set();
    function precargar(clave) {
      if (precargados.has(clave)) return;
      precargados.add(clave);
      for (const img of $$('img', paneles.get(clave) ?? document.createDocumentFragment())) {
        img.loading = 'eager';
      }
    }

    function abrir(clave) {
      const panel = paneles.get(clave);
      if (!panel) return;

      for (const [k, p] of paneles) p.hidden = k !== clave;
      for (const d of disparadores) {
        d.setAttribute('aria-expanded', String(d.dataset.navTrigger === clave));
      }

      if (abierto === null) {
        /* La cabecera se encoge al hacer scroll: su altura no es una constante.
           Se mide al abrir, no se supone. */
        const cabecera = document.querySelector('.header');
        if (cabecera) {
          superficie.style.setProperty('--navx-top',
            `${Math.round(cabecera.getBoundingClientRect().bottom)}px`);
        }
        superficie.hidden = false;
        if (scrim) scrim.hidden = false;
        // Un fotograma con el atributo puesto y `hidden` recién quitado: sin
        // esta lectura el navegador no calcula el estado cerrado y no hay
        // recorrido que animar, sólo un salto.
        getComputedStyle(superficie).opacity;
        superficie.dataset.open = 'true';
        if (scrim) scrim.dataset.open = 'true';
        document.documentElement.dataset.navOpen = 'true';
      }
      // Cambiar de entrada con la superficie ya abierta: sólo cambia el dentro.
      superficie.dataset.panel = clave;
      abierto = clave;
    }

    function cerrar({ devolverFoco = false } = {}) {
      if (abierto === null) return;
      const anterior = abierto;
      abierto = null;
      superficie.dataset.open = 'false';
      if (scrim) scrim.dataset.open = 'false';
      delete document.documentElement.dataset.navOpen;
      for (const d of disparadores) d.setAttribute('aria-expanded', 'false');

      // Se espera al final de la transición para esconderlo de verdad: si se
      // esconde ya, la salida no se ve.
      const fin = () => {
        if (abierto === null) {
          superficie.hidden = true;
          if (scrim) scrim.hidden = true;
        }
        superficie.removeEventListener('transitionend', fin);
      };
      superficie.addEventListener('transitionend', fin);
      setTimeout(fin, 600);   // red de seguridad: pestaña oculta, sin frames

      if (devolverFoco) {
        disparadores.find((d) => d.dataset.navTrigger === anterior)?.focus();
      }
    }

    /* ── puntero ──────────────────────────────────────────────────────── */

    const fino = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    for (const d of disparadores) {
      const clave = d.dataset.navTrigger;

      d.addEventListener('pointerenter', () => {
        if (!fino()) return;
        precargar(clave);
        cancelar();
        // Con la superficie ya abierta el cambio es inmediato: la intención ya
        // está demostrada, y esperar otra vez se siente como pereza.
        if (abierto !== null) { abrir(clave); return; }
        temporizador = setTimeout(() => abrir(clave), ABRIR);
      });

      d.addEventListener('focus', () => { precargar(clave); abrir(clave); });

      d.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' && abierto === clave) {
          e.preventDefault();
          $('a, button', paneles.get(clave))?.focus();
        }
        if (e.key === 'Escape') cerrar({ devolverFoco: true });
      });
    }

    /* Salir de la cabecera o del panel cierra, pero con margen: entre el
       enlace y la superficie hay un hueco de píxeles que el ratón cruza, y
       cerrar ahí convierte el menú en un parpadeo. */
    const zona = [superficie, ...disparadores.map((d) => d.closest('.header') ?? d)];
    for (const el of new Set(zona)) {
      el.addEventListener('pointerleave', () => {
        if (!fino() || abierto === null) return;
        cancelar();
        temporizador = setTimeout(() => cerrar(), CERRAR);
      });
      el.addEventListener('pointerenter', cancelar);
    }

    /* ── teclado y clic fuera ─────────────────────────────────────────── */

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && abierto !== null) cerrar({ devolverFoco: true });
    });

    document.addEventListener('pointerdown', (e) => {
      if (abierto === null) return;
      if (superficie.contains(e.target) || e.target.closest?.('[data-nav-trigger]')) return;
      cerrar();
    });

    // Salir del panel con el tabulador cierra: si el foco ya no está dentro,
    // la superficie no está ayudando a nadie.
    superficie.addEventListener('focusout', (e) => {
      if (abierto === null) return;
      const destino = e.relatedTarget;
      if (!destino) return;
      if (superficie.contains(destino) || destino.closest?.('[data-nav-trigger]')) return;
      cerrar();
    });

    // Navegar cierra: el enlace sigue siendo un enlace.
    superficie.addEventListener('click', (e) => {
      if (e.target.closest('a')) cerrar();
    });

    return cerrar;
  }

  return { initNavSurface };
})();

__EDI["core-catalog.js"] = (function () {
  'use strict';

  /**
   * Lógica de catálogo: filtrar y ordenar. Funciones puras, sin DOM y sin red,
   * para que se puedan probar solas y para que ShopifyProductRepository pueda
   * reutilizarlas o delegarlas al servidor sin tocar la UI.
   *
   * Los criterios son deliberadamente los que Shopify Search & Discovery sabe
   * hacer: no se inventa nada que luego no se pueda migrar.
   */

  /** ¿El producto tiene alguna variante disponible con esta talla? */
  function hasSize(product, size) {
    return product.variants.some((v) => v.options?.Taille === size && v.available);
  }

  function hasColor(product, color) {
    return product.variants.some((v) => v.options?.Couleur === color);
  }

  function inStock(product) {
    return product.variants.some((v) => v.available);
  }

  function matchesFilters(product, filters = {}) {
    const { category = [], size = [], color = [], price = null, availability = [] } = filters;

    if (category.length && !category.includes(product.type)) return false;
    if (size.length && !size.some((s) => hasSize(product, s))) return false;
    if (color.length && !color.some((c) => hasColor(product, c))) return false;

    if (price) {
      const low = product.price;
      if (price.min != null && low < price.min) return false;
      if (price.max != null && low > price.max) return false;
    }

    if (availability.includes('in-stock') && !inStock(product)) return false;

    return true;
  }

  const SORTS = ['featured', 'newest', 'price-asc', 'price-desc'];

  function sortProducts(products, sort = 'featured') {
    const list = products.slice();
    switch (sort) {
      case 'newest':
        return list.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      case 'price-asc':
        return list.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return list.sort((a, b) => b.price - a.price);
      default:
        return list; // «featured» = el orden de la colección, como en Shopify
    }
  }

  /** Cuenta cuántos filtros hay puestos, para el «FILTER (2)». */
  function countFilters(filters = {}) {
    let n = 0;
    for (const [key, value] of Object.entries(filters)) {
      if (key === 'price') n += value && (value.min != null || value.max != null) ? 1 : 0;
      else n += Array.isArray(value) ? value.length : 0;
    }
    return n;
  }

  return { hasSize, hasColor, inStock, matchesFilters, SORTS, sortProducts, countFilters };
})();

__EDI["adapters-product-repository.js"] = (function () {
  'use strict';
  const { matchesFilters, sortProducts } = __EDI["core-catalog.js"];
  /**
   * ProductRepository — la UI nunca sabe de dónde vienen los productos.
   *
   * Hoy   → LocalProductRepository, sobre data/*.json (instantánea de solo lectura).
   * Mañana → ShopifyProductRepository, sobre Liquid preprocesado o Storefront API.
   *
   * Para cambiar de uno a otro basta con implementar la misma interfaz y
   * reasignar el export de abajo. Ni un solo componente cambia.
   *
   *   all()             → Product[]
   *   byId(id)          → Product | null
   *   byHandle(handle)  → Product | null
   *   byCollection(handle, {limit, offset, filters, sort}) → {items, total}
   *   customizable()    → Product[]
   *   related(id, n)    → Product[]
   *   facets(handle)    → {categories, sizes, colors, priceRange}
   */


  let cache = null;

  async function load() {
    if (cache) return cache;
    const [products, collections, colors] = await Promise.all([
      fetch('/data/products.json').then((r) => r.json()),
      fetch('/data/collections.json').then((r) => r.json()),
      fetch('/data/color-map.json').then((r) => r.json()).catch(() => ({ colors: {} })),
    ]);
    cache = {
      products: products.products,
      collections: collections.collections,
      colors: colors.colors ?? {},
    };
    return cache;
  }

  const LocalProductRepository = {
    async all() {
      return (await load()).products;
    },

    async byId(id) {
      return (await this.all()).find((p) => p.id === id) ?? null;
    },

    async byHandle(handle) {
      return (await this.all()).find((p) => p.handle === handle) ?? null;
    },

    async collections() {
      return (await load()).collections;
    },

    async collection(handle) {
      return (await load()).collections.find((c) => c.handle === handle) ?? null;
    },

    /** Devuelve {items, total}: el total es antes de paginar, para el «Load more». */
    async byCollection(handle, { limit = Infinity, offset = 0, filters = {}, sort = 'featured' } = {}) {
      const { products, collections } = await load();
      const collection = collections.find((c) => c.handle === handle);
      if (!collection) return { items: [], total: 0 };

      const index = new Map(products.map((p) => [p.id, p]));
      const ordered = collection.productIds.map((id) => index.get(id)).filter(Boolean);
      const filtered = ordered.filter((p) => matchesFilters(p, filters));
      const sorted = sortProducts(filtered, sort);

      return { items: sorted.slice(offset, offset + limit), total: sorted.length };
    },

    async customizable() {
      return (await this.all()).filter((p) => p.customizable);
    },

    /**
     * Relacionados. Hoy: mismo tipo, sin repetir, completando por precio cercano.
     * Mañana: recomendaciones de Shopify (`/recommendations/products.json`).
     */
    async related(id, count = 4) {
      const all = await this.all();
      const base = all.find((p) => p.id === id);
      if (!base) return [];
      const rest = all.filter((p) => p.id !== id && p.universe === base.universe);
      const sameType = rest.filter((p) => p.type === base.type);
      const byPrice = rest
        .filter((p) => !sameType.includes(p))
        .sort((a, b) => Math.abs(a.price - base.price) - Math.abs(b.price - base.price));
      return [...sameType, ...byPrice].slice(0, count);
    },

    /** Valores disponibles para los filtros, calculados desde el propio catálogo. */
    async facets(handle) {
      const { items } = await this.byCollection(handle);
      const sizes = new Set();
      const colors = new Set();
      const categories = new Set();
      let min = Infinity;
      let max = 0;

      for (const p of items) {
        categories.add(p.type);
        min = Math.min(min, p.price);
        max = Math.max(max, p.priceMax ?? p.price);
        for (const option of p.options) {
          if (option.name === 'Taille') option.values.forEach((v) => sizes.add(v));
          if (option.name === 'Couleur') option.values.forEach((v) => colors.add(v));
        }
      }

      const ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
      return {
        categories: [...categories].sort(),
        sizes: [...sizes].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)),
        colors: [...colors].sort(),
        priceRange: items.length ? { min, max } : { min: 0, max: 0 },
      };
    },

    async colorMap() {
      return (await load()).colors;
    },
  };

  /**
   * ShopifyProductRepository — el producto que Liquid ya ha escrito en la página.
   *
   * Esto es lo que el comentario de arriba anunciaba desde el primer día: la
   * misma interfaz, otro origen. Y el origen no es una petición: es un
   * `<script type="application/json">` que `snippets/product-json.liquid` ha
   * dejado en el HTML con la forma de siempre. Cero peticiones, cero espera, y
   * la traducción entre la forma de Shopify y la del tema vive en UN archivo de
   * Liquid que se puede probar con un fixture.
   *
   * Lo que NO hace: buscar por colección, calcular facetas ni recomendar. En una
   * tienda eso lo pinta el servidor —y tiene que pintarlo, o un buscador no ve
   * los enlaces—. Devolver vacío es lo correcto: mejor que un componente no
   * encuentre nada a que se ponga a pedir `/data/products.json`, que allí no
   * existe y daría 404 en cada carga.
   */
  function productoEmbebido() {
    if (typeof document === 'undefined') return null;
    const etiqueta = document.querySelector('[data-producto-json]');
    if (!etiqueta) return null;
    try {
      return JSON.parse(etiqueta.textContent);
    } catch (error) {
      return null;
    }
  }

  const ShopifyProductRepository = {
    async all() {
      const p = productoEmbebido();
      return p ? [p] : [];
    },
    async byId(id) {
      const p = productoEmbebido();
      return p && p.id === id ? p : null;
    },
    async byHandle(handle) {
      const p = productoEmbebido();
      return p && p.handle === handle ? p : null;
    },
    async collections() { return []; },
    async collection() { return null; },
    async byCollection() { return { items: [], total: 0 }; },
    async customizable() { return []; },
    async related() { return []; },
    async facets() {
      return { categories: [], sizes: [], colors: [], priceRange: { min: 0, max: 0 } };
    },
    async colorMap() { return {}; },
  };

  /* La decisión se toma EN CADA LLAMADA, no al cargar el módulo.
   *
   * Al cargar sería más limpio de leer y estaría mal: los módulos se importan
   * antes de que el navegador haya terminado el cuerpo, así que la etiqueta con
   * el producto puede no existir todavía y el tema se quedaría con el
   * repositorio equivocado para el resto de la vida de la página. */
  const metodos = Object.keys(LocalProductRepository);
  const ProductRepository = Object.fromEntries(
    metodos.map((nombre) => [
      nombre,
      (...args) =>
        (productoEmbebido() ? ShopifyProductRepository : LocalProductRepository)[nombre](...args),
    ])
  );

  return { LocalProductRepository, ShopifyProductRepository, ProductRepository };
})();

__EDI["core-format.js"] = (function () {
  'use strict';

  /**
   * Formato de precio. Base CHF; preparado para Shopify Markets.
   *
   * El franco suizo se escribe con PUNTO decimal y apóstrofo de millares:
   * CHF 44.90, CHF 1'234.50. Y así en los cuatro idiomas — es la moneda la que
   * manda el formato, no el idioma de la interfaz. Usar la coma francesa aquí
   * hacía que el HTML dijera 44.90 y el JavaScript lo reescribiera a 44,90 en
   * cuanto cargaba: el mismo precio con dos caras.
   */

  const FORMATTER = new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  /** 44.9 → "CHF 44.90" · 1234.5 → "CHF 1'234.50" */
  function price(amount, { currency = 'CHF' } = {}) {
    return `${currency} ${FORMATTER.format(amount)}`;
  }

  return { price };
})();

__EDI["core-events.js"] = (function () {
  'use strict';

  /**
   * Capa de analítica. §105: los eventos existen desde el primer día,
   * pero NO se envía nada mientras no haya un proveedor configurado
   * y consentimiento. En desarrollo solo se registran en memoria.
   */

  const QUEUE = [];
  let sink = null;

  const EVENTS = Object.freeze({
    VIEW_COLLECTION: 'view_collection',
    SELECT_PRODUCT: 'select_product',
    VIEW_PRODUCT: 'view_product',
    OPEN_SIZE_GUIDE: 'open_size_guide',
    REMOVE_FROM_CART: 'remove_from_cart',
    VIEW_CART: 'view_cart',
    SELECT_VARIANT: 'select_variant',
    ADD_TO_CART: 'add_to_cart',
    OPEN_CUSTOMIZER: 'open_customizer',
    UPLOAD_DESIGN: 'upload_design',
    ADD_TEXT: 'add_text',
    GENERATE_AI: 'generate_ai',
    SAVE_DESIGN: 'save_design',
    COMPLETE_CUSTOMIZATION: 'complete_customization',
    BEGIN_CHECKOUT: 'begin_checkout',
    PURCHASE: 'purchase',
  });

  /** Conecta un destino real (GA4, Meta, Shopify) — solo tras consentimiento. */
  function connect(fn) {
    sink = fn;
    while (QUEUE.length) sink(QUEUE.shift());
  }

  function track(name, payload = {}) {
    const event = { name, payload, at: Date.now() };
    if (sink) sink(event);
    else QUEUE.push(event);
    if (document.documentElement.dataset.mode === 'development') {
      console.debug('[edisamp:event]', name, payload);
    }
  }

  const pending = () => QUEUE.slice();

  return { EVENTS, connect, track, pending };
})();

__EDI["components-cart-drawer.js"] = (function () {
  'use strict';
  const { $, $$, el } = __EDI["core-dom.js"];
  const { announce, createLayer } = __EDI["core-a11y.js"];
  const { CartAdapter } = __EDI["adapters-cart-adapter.js"];
  const { ProductRepository } = __EDI["adapters-product-repository.js"];
  const { price: formatPrice } = __EDI["core-format.js"];
  const { t, locale } = __EDI["core-i18n.js"];
  const { track, EVENTS } = __EDI["core-events.js"];
  /**
   * Cajón del carrito.
   *
   * Lo único que tiene que pasar aquí es llegar al checkout. Sin storytelling,
   * sin cuentas atrás, sin «X personas están comprando». Un solo bloque de
   * sugerencia y para de contar.
   *
   * El carrito se pinta desde el estado del CartAdapter, no desde el DOM: da
   * igual quién lo cambie (esta página, otra pestaña, mañana Shopify), la vista
   * escucha 'edisamp:cart' y se redibuja.
   */


  function initCartDrawer() {
    const root = $('[data-cart]');
    if (!root) return;

    /* ¿QUIÉN SABE QUÉ HAY EN LA BOLSA?
     *
     * En el prototipo, `CartAdapter` sobre `localStorage`: no hay servidor, y
     * redibujar desde ahí es lo correcto.
     *
     * En una tienda, NO. Ahí el carrito es verdad del servidor y Liquid ya ha
     * escrito las líneas en el HTML. Redibujar aquí desde `localStorage` las
     * BORRA: el cajón salía vacío teniendo dos artículos dentro. Se vio en
     * pantalla; ningún test sabía que eso podía pasar.
     *
     * Así que cuando el servidor manda, este módulo hace UNA cosa: abrir y
     * cerrar. No pinta, no cuenta, no sugiere y no toca el checkout — el botón
     * ya es un `submit` de verdad. */
    const mandaElServidor = root.dataset.cartServidor !== undefined;

    const layer = createLayer(root, {
      onOpen: () =>
        track(EVENTS.VIEW_CART, {
          lines: mandaElServidor ? undefined : CartAdapter.read().lines.length,
        }),
    });
    const list = $('[data-cart-lines]', root);
    const empty = $('[data-cart-empty]', root);
    const foot = $('[data-cart-foot]', root);
    const subtotal = $('[data-cart-subtotal]', root);
    const countEl = $('[data-cart-count]', root);
    const undoBox = $('[data-cart-undo]', root);
    const upsell = $('[data-cart-upsell]', root);

    const money = (n) => formatPrice(n, { locale: locale() });

    /* ------------------------------------------------------------- render - */

    function render(reason) {
      if (mandaElServidor) return;
      const state = CartAdapter.read();
      const lines = state.lines;

      if (list) {
        list.textContent = '';
        for (const line of lines) list.append(renderLine(line));
      }

      const isEmpty = lines.length === 0;
      if (empty) empty.hidden = !isEmpty;
      if (foot) foot.hidden = isEmpty;
      if (list) list.hidden = isEmpty;
      if (upsell) upsell.hidden = isEmpty;

      if (subtotal) subtotal.textContent = money(CartAdapter.subtotal());
      if (countEl) countEl.textContent = t('cart.count', { count: CartAdapter.count() });
      if (undoBox) undoBox.hidden = !CartAdapter.hasUndo();

      if (reason === 'add') announce(t('cart.added'));
      if (reason === 'remove') announce(t('cart.removed'));
    }

    function renderLine(line) {
      const variant = [line.options?.Couleur, line.options?.Taille].filter(Boolean).join(' / ');

      const media = el('div', { class: 'cline__media' }, [
        line.image
          ? el('img', { src: line.image, alt: '', width: 200, height: 250, loading: 'lazy' })
          : null,
      ]);

      const qty = el('div', { class: 'qty', role: 'group', 'aria-label': t('cart.quantityFor', { name: line.name }) }, [
        el('button', {
          type: 'button',
          'aria-label': t('cart.decrease'),
          disabled: line.quantity <= 1,
          onClick: () => CartAdapter.setQuantity(line.key, line.quantity - 1),
        }, ['−']),
        el('span', { class: 'qty__value', 'aria-live': 'off' }, [String(line.quantity)]),
        el('button', {
          type: 'button',
          'aria-label': t('cart.increase'),
          onClick: () => CartAdapter.setQuantity(line.key, line.quantity + 1),
        }, ['+']),
      ]);

      const body = el('div', { class: 'cline__body' }, [
        el('div', { class: 'cline__top' }, [
          el('p', { class: 'cline__name' }, [line.name]),
          el('p', { class: 'cline__price' }, [money(line.price * line.quantity)]),
        ]),
        variant ? el('p', { class: 'cline__variant' }, [variant]) : null,
        // Personalizado: se marca y se puede ver. El backend del diseño no
        // existe todavía; la estructura de datos sí.
        line.customization
          ? el('span', { class: 'cline__custom' }, [t('cart.customDesign')])
          : null,
        line.customization
          ? el('a', { class: 'cline__view', href: `/custom/?design=${line.customization.id}` },
              [t('cart.viewDesign')])
          : null,
        el('div', { class: 'cline__foot' }, [
          qty,
          el('button', {
            type: 'button',
            class: 'cline__remove',
            onClick: () => CartAdapter.remove(line.key),
          }, [t('cart.remove')]),
        ]),
      ]);

      return el('article', { class: 'cline', dataset: { line: line.key } }, [media, body]);
    }

    /* ------------------------------------------------------------ upsell -- */

    async function renderUpsell() {
      if (!upsell) return;
      const box = $('[data-upsell-list]', upsell);
      if (!box) return;
      if (mandaElServidor) return;
      const lines = CartAdapter.read().lines;
      if (!lines.length) return;

      // Se prueba línea a línea. Una pieza personalizada no tiene relacionados
      // —no está en el catálogo— y quedarse en la primera línea dejaba sin
      // sugerencias a quien sólo lleva un diseño suyo en la bolsa.
      let related = [];
      for (const line of lines) {
        related = await ProductRepository.related(line.productId, 2);
        if (related.length) break;
      }
      box.textContent = '';

      // Un producto sin relacionados deja el encabezado flotando sobre nada.
      // «Complétez la tenue» sin nada que completar es peor que no decirlo.
      upsell.hidden = related.length === 0;
      if (!related.length) return;
      for (const p of related) {
        box.append(
          el('a', { class: 'upsell__item', href: `/products/${p.handle}/` }, [
            el('img', { src: p.media?.[0]?.url ?? '', alt: '', width: 104, height: 130, loading: 'lazy' }),
            el('span', { class: 'upsell__name' }, [p.name]),
            el('span', { class: 'upsell__price' }, [money(p.price)]),
          ])
        );
      }
    }

    /* ------------------------------------------- refresco desde el servidor - */

    /**
     * Vuelve a pedir el cajón a Shopify y mete lo que cambió.
     *
     * EL FALLO QUE ARREGLA. Con `data-cart-servidor` este módulo no pinta: las
     * líneas las escribe Liquid al cargar la página, que es lo correcto porque
     * el carrito es verdad del servidor. Pero « añadir » no recarga la página,
     * así que nadie volvía a pedir ese HTML: Shopify guardaba la pieza, el
     * contador de la cabecera subía a 1, y el cajón seguía enseñando lo que
     * había al abrir la página — vacío.
     *
     * En pantalla eso era « Panier 1 » arriba y « VOTRE PANIER EST VIDE »
     * debajo, a la vez, con la pieza ya dentro. Quien ve eso no termina la
     * compra: cree que no se guardó.
     *
     * SE CAMBIA LO QUE CAMBIA, NO EL CAJÓN ENTERO. Reemplazar el `innerHTML` de
     * la raíz se lleva por delante los oyentes de cerrar y del velo, que se
     * enganchan uno a uno más abajo. Se tocan sólo cuatro zonas, y ninguna los
     * contiene. Dentro de las líneas no hace falta volver a enganchar nada: los
     * controles de cantidad y de quitar son `<a href>` de Shopify y el checkout
     * es un `<form>` con `submit` — HTML nativo, sin JavaScript detrás.
     */
    async function refrescarDesdeServidor() {
      let doc;
      try {
        const r = await fetch('/cart?view=drawer', {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      } catch {
        /* Si la red falla no se toca nada: el cajón se queda como está y la
           página sigue viva. El cliente puede recargar y lo verá igual. */
        return;
      }

      const fresco = doc.querySelector('[data-cart]');
      if (!fresco) return;

      const traer = (sel, como) => {
        const nuevo = $(sel, fresco);
        const viejo = $(sel, root);
        if (nuevo && viejo) como(viejo, nuevo);
      };

      traer('[data-cart-lines]', (v, n) => { v.innerHTML = n.innerHTML; });
      traer('[data-cart-count]', (v, n) => { v.textContent = n.textContent; });
      traer('[data-cart-subtotal]', (v, n) => { v.textContent = n.textContent; });
      /* `hidden` es lo que decide si se ve « votre panier est vide » o el pie
         con el botón de pagar. Lo calcula Liquid con `cart.item_count`. */
      traer('[data-cart-empty]', (v, n) => { v.hidden = n.hidden; });
      traer('[data-cart-foot]', (v, n) => { v.hidden = n.hidden; });
    }

    /* ------------------------------------------------------------ eventos - */

    /* `agregar.js` lo lanza cuando Shopify ya ha confirmado el añadido. */
    if (mandaElServidor) {
      document.addEventListener('carrito:cambiado', () => { refrescarDesdeServidor(); });
    }

    document.addEventListener('edisamp:cart', (event) => {
      render(event.detail?.reason);
      renderUpsell();
    });

    document.addEventListener('edisamp:cart:open', () => layer.open());

    $$('[data-cart-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        layer.open(e.currentTarget);
      })
    );

    $$('[data-cart-close]', root).forEach((b) => b.addEventListener('click', () => layer.close()));
    $('.layer__scrim', root)?.addEventListener('click', () => layer.close());

    $('[data-cart-undo-button]', root)?.addEventListener('click', async () => {
      await CartAdapter.undoRemove();
      announce(t('cart.restored'));
    });

    if (!mandaElServidor) $('[data-checkout]', root)?.addEventListener('click', async () => {
      track(EVENTS.BEGIN_CHECKOUT, { lines: CartAdapter.read().lines.length });
      const result = await CartAdapter.checkout();
      if (!result.ok) {
        // En standalone no hay checkout, y se dice claramente en vez de fingir.
        const msg = $('[data-checkout-note]', root);
        if (msg) msg.hidden = false;
        announce(t('cart.checkoutUnavailable'));
      }
    });

    render();
    renderUpsell();
  }

  return { initCartDrawer };
})();

__EDI["experience-fly-to-cart.js"] = (function () {
  'use strict';
  const { tokenDuration } = __EDI["core-motion.js"];
  const { murcielagosAlAterrizar, capsulaDeTemporada } = __EDI["experience-temporada.js"];
  /**
   * El gesto de añadir a la bolsa.
   *
   * Antes: un clon de la fotografía entera viajaba en arco corto hasta el
   * icono. Funcionaba y era exactamente lo que hace cualquier tienda.
   *
   * Ahora la secuencia cuenta algo: **el producto sale del escenario y entra
   * en la bolsa**.
   *
   *   1. el botón se comprime — acuse de recibo inmediato
   *   2. el escenario se retira un paso: gana profundidad y suelta la pieza
   *   3. la pieza se despega y viaja por una curva real
   *   4. se comprime a medida que se aleja, y gira apenas
   *   5. la bolsa acusa el aterrizaje, una vez
   *   6. el panel entra
   *
   * Cuatro decisiones que lo mantienen del lado de lo elegante:
   *
   *   1. NO bloquea nada. La bolsa ya está actualizada cuando el vuelo
   *      empieza; si el cliente hace clic en otra cosa a mitad, no pasa nada.
   *   2. Vuela un CLON, nunca el original.
   *   3. Cuando lo que se está mirando es una persona, NO vuela la persona:
   *      vuela la pieza. Ver una fotografía de alguien encogerse hasta el
   *      icono es exactamente lo que esta fase venía a no hacer.
   *   4. Con `prefers-reduced-motion` no hay vuelo: la bolsa confirma igual.
   *      La confirmación es el mensaje; el vuelo sólo es la forma.
   *
   * Sin rebotes de dibujo animado, sin confeti, sin carga fingida.
   */


  const REDUCIDO = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const ESTRECHO = () =>
    window.matchMedia?.('(max-width: 767px)').matches ?? false;

  /* El botón de la bolsa en el header. El atributo es `data-cart-open`, que es
     como lo llama el header desde el principio; se busca por él y no por un
     nombre inventado. */
  const iconoCarrito = () => document.querySelector('[data-cart-open]');

  /** Duración dentro del rango pedido, leída del token. */
  const duracion = () =>
    Math.min(tokenDuration('--motion-fly', { fallback: 560 }) || 560, 700);

  /** Salida suave: rápido al principio, se posa al final. */
  const suavizar = (t) => 1 - (1 - t) ** 3;

  /**
   * La curva. Una recta se lee como un deslizamiento; un arco corto se lee
   * como un objeto que sale de una escena y entra en otra.
   *
   * Bézier cuadrática con rects REALES de origen y destino. Ninguna coordenada
   * inventada: si el header cambia de sitio, la curva cambia sola.
   */
  function trayectoria(a, b, { pasos = 26, alzada = 54, escalaFinal = 0.18 } = {}) {
    const x0 = a.left + a.width / 2;
    const y0 = a.top + a.height / 2;
    const x1 = b.left + b.width / 2;
    const y1 = b.top + b.height / 2;

    // El punto de control queda pasada la mitad y por encima de los dos
    // extremos: la pieza sale hacia arriba y cae sobre la bolsa.
    const cx = x0 + (x1 - x0) * 0.58;
    const cy = Math.min(y0, y1) - alzada;

    const marcos = [];
    for (let i = 0; i <= pasos; i++) {
      const t = i / pasos;
      const u = 1 - t;
      const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
      const y = u * u * y0 + 2 * u * t * cy + t * t * y1;

      // Se comprime CON la distancia, no de golpe al final.
      const escala = 1 + (escalaFinal - 1) * suavizar(t);

      /* Un giro de tres grados que va y vuelve. No es un spin: es el peso de
         algo que se lanza. Un objeto que da vueltas se lee como un icono de
         videojuego. */
      const giro = (-3 * Math.sin(Math.PI * t)).toFixed(2);

      // La opacidad sólo cae al final, ya encima de la bolsa.
      const opacidad = t < 0.82 ? 1 : 1 - ((t - 0.82) / 0.18) * 0.85;

      marcos.push({
        offset: t,
        transform: `translate(${(x - x0).toFixed(1)}px, ${(y - y0).toFixed(1)}px)`
          + ` scale(${escala.toFixed(4)}) rotate(${giro}deg)`,
        opacity: opacidad.toFixed(3),
      });
    }
    return marcos;
  }

  /**
   * @param {HTMLElement} origen   el escenario del producto
   * @param {object}      [opts]
   * @param {string}      [opts.token]  qué imagen vuela. Cuando el escenario
   *   enseña a una persona, aquí llega la pieza sola del mismo color.
   * @param {HTMLElement} [opts.destino]
   */
  function flyToCart(origen, opts = {}) {
    // Compatibilidad: antes el segundo argumento era el destino.
    const config = opts instanceof HTMLElement ? { destino: opts } : opts;
    const destino = config.destino ?? iconoCarrito();

    comprimirBoton(config.boton);

    if (!origen || !destino || REDUCIDO()) {
      pulsarCarrito(destino);
      return Promise.resolve(false);
    }

    const a = origen.getBoundingClientRect();
    const b = destino.getBoundingClientRect();
    if (!a.width || !b.width) {
      pulsarCarrito(destino);
      return Promise.resolve(false);
    }

    /* La pieza que vuela. Si llega un `token` —porque el escenario enseña a
       una persona, o porque existe un recorte— se construye una imagen nueva
       con ese origen. Si no, se clona lo que hay. */
    const clon = config.token ? piezaDe(config.token, origen) : origen.cloneNode(true);
    clon.removeAttribute('id');
    clon.setAttribute('aria-hidden', 'true');
    // Un clon que sigue siendo enfocable dejaría al teclado atrapado en algo
    // que está a punto de desaparecer.
    clon.querySelectorAll?.('[id],[tabindex]').forEach((n) => {
      n.removeAttribute('id'); n.setAttribute('tabindex', '-1');
    });
    /* EN TEMPORADA, LA PIEZA SE TRANSFORMA EN MURCIÉLAGO A MITAD DEL VUELO.

       Discutí esto y perdí, y con razón: Franklin no pidió cambiar la foto por
       un murciélago —eso sí borraría la información— sino que la foto SE
       CONVIERTA en uno. Es otra cosa. Durante el primer tercio del recorrido lo
       que vuela sigue siendo su producto, que es cuando el ojo necesita saber
       qué ha metido en la bolsa; después se vuelve silueta y se va batiendo las
       alas. La información llega y el truco también.

       Para que quepan los dos hace falta un envoltorio: un `<img>` no admite
       hijos, y el clon muchas veces es exactamente eso. Vuela la cápsula. */
    const capsula = capsulaDeTemporada(clon);
    Object.assign(capsula.style, {
      position: 'fixed', left: `${a.left}px`, top: `${a.top}px`,
      width: `${a.width}px`, height: `${a.height}px`,
      margin: '0', zIndex: '9999', pointerEvents: 'none',
      borderRadius: getComputedStyle(origen).borderRadius || '4px',
      objectFit: 'cover', overflow: 'visible',
      willChange: 'transform, opacity',
    });
    document.body.append(capsula);

    /* El escenario se retira un paso mientras la pieza se va. Es lo que
       convierte el vuelo en «esto ha salido de ahí» en vez de «ha aparecido
       una miniatura». La persona, si la hay, se queda: no se la manda al
       carrito. */
    origen.dataset.xpReleasing = 'true';

    /* En un móvil la pieza ocupa casi toda la pantalla y el icono está justo
       encima: el arco no se llega a leer —no hay ancho donde dibujarlo— y sí
       se nota el coste de animar un clon enorme. Ahí el recorrido es más corto
       y más plano. Sigue diciendo de dónde a dónde ha ido la pieza. */
    const estrecho = ESTRECHO();
    const ms = duracion();
    const duracionReal = estrecho ? Math.round(ms * 0.72) : ms;

    const anim = capsula.animate(
      trayectoria(a, b, {
        alzada: estrecho ? 18 : 54,
        escalaFinal: Math.max(0.06, Math.min((b.width / a.width) * 1.6, 0.26)),
      }),
      { duration: duracionReal, easing: 'linear', fill: 'forwards' }
    );

    /* Red de seguridad: `anim.finished` no resuelve mientras la pestaña está
       oculta, porque el navegador detiene el ciclo de fotogramas. Sin esto, un
       cliente que cambia de pestaña a mitad del vuelo vuelve y se encuentra
       una miniatura flotando sobre la página. El temporizador siempre limpia. */
    let limpio = false;
    const aterrizar = () => {
      if (limpio) return true;
      limpio = true;
      capsula.remove();
      delete origen.dataset.xpReleasing;
      // La bolsa acusa el ATERRIZAJE, no el clic. Es la diferencia entre que
      // el icono confirme algo que ya ha pasado y que reciba algo.
      pulsarCarrito(destino);
      /* Y si hay temporada, se espantan tres murciélagos. DESPUÉS del
         aterrizaje, nunca en lugar de la foto: la foto volando es la que dice
         qué ha entrado en la bolsa, y eso no se cambia por un adorno. Fuera de
         temporada esta línea no hace nada. */
      murcielagosAlAterrizar(destino);
      return true;
    };
    const porTiempo = new Promise((r) => setTimeout(() => r(aterrizar()), duracionReal + 250));

    return Promise.race([
      anim.finished.catch(() => {}).then(aterrizar),
      porTiempo,
    ]);
  }

  /** Una pieza cuadrada con la fotografía del producto dentro. */
  function piezaDe(url, origen) {
    const caja = document.createElement('div');
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.decoding = 'async';
    Object.assign(img.style, {
      width: '100%', height: '100%', objectFit: 'cover', display: 'block',
    });
    caja.append(img);
    caja.style.background = getComputedStyle(origen).backgroundColor || 'transparent';
    return caja;
  }

  /** El botón acusa recibo antes que nada. Noventa milisegundos. */
  function comprimirBoton(boton) {
    if (!boton) return;
    boton.dataset.committing = 'true';
    setTimeout(() => delete boton.dataset.committing,
      tokenDuration('--motion-instant', { fallback: 90 }) * 2);
  }

  /** El icono acusa recibo. Es lo que confirma; el vuelo sólo acompaña. */
  function pulsarCarrito(destino = iconoCarrito()) {
    if (!destino) return;
    destino.dataset.reacting = 'true';
    const quitar = () => delete destino.dataset.reacting;
    if (REDUCIDO()) { setTimeout(quitar, 200); return; }
    destino.addEventListener('animationend', quitar, { once: true });
    setTimeout(quitar, 900);   // por si la animación no llega a correr
  }

  return { flyToCart, pulsarCarrito };
})();

__EDI["components-agregar.js"] = (function () {
  'use strict';
  const { t } = __EDI["core-i18n.js"];
  const { flyToCart } = __EDI["experience-fly-to-cart.js"];
  const { selloDeTemporada } = __EDI["experience-temporada.js"];
  /**
   * Añadir a la bolsa sin salir de la página.
   *
   * POR QUÉ
   * -------
   * El formulario de compra es un `POST` a `/cart/add`, y Shopify responde con
   * una redirección a `/cart`. Funciona, y es lo correcto cuando no hay
   * JavaScript — pero con él, sacar a alguien de la ficha para enseñarle una
   * tabla es la forma más rápida de terminar una visita: quien quería seguir
   * mirando tiene que volver atrás, y quien añadió por error ni se entera de lo
   * que lleva.
   *
   * Aquí el envío se intercepta, la pieza se añade por detrás y aparece un
   * aviso que dice QUÉ se ha añadido y CUÁNTO hay en la bolsa. Dos salidas:
   * seguir mirando —que es lo que casi todo el mundo quiere— o ir a pagar.
   *
   * SI ALGO FALLA, SE DEJA PASAR. Un fallo de red, una variante agotada entre
   * medias o un `fetch` bloqueado no pueden dejar el botón muerto: el
   * formulario se envía como siempre y Shopify se encarga.
   */


  const AVISO = 'agregado';

  function dinero(centimos, moneda) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency', currency: moneda,
      }).format(centimos / 100);
    } catch {
      return (centimos / 100).toFixed(2) + ' ' + moneda;
    }
  }

  /**
   * Cuánto falta para el envío gratis, con el carrito ya actualizado.
   *
   * Repinta las barras que ya estén en la página —el cajón, el carrito— y
   * devuelve el HTML de la que va dentro del aviso. Sin umbral, no devuelve
   * nada y nadie se entera de que esto existe.
   */
  function envioGratis(bolsa, moneda) {
    const min = Number(document.body.dataset.envioMin || 0);
    if (!min) return '';

    const falta = min - bolsa.total_price;
    const pct = falta > 0 ? Math.round((bolsa.total_price / min) * 100) : 100;
    const texto = falta > 0
      ? t('cart.free_shipping_left', { amount: dinero(falta, moneda) })
      : t('cart.free_shipping_reached');

    for (const barra of document.querySelectorAll('[data-envio-gratis]')) {
      const p = barra.querySelector('[data-envio-texto]');
      const b = barra.querySelector('[data-envio-barra]');
      if (p) p.textContent = texto;
      if (b) { b.value = pct; b.textContent = pct + '%'; }
    }

    return `<div class="envio"><p class="envio__texto">${texto}</p>`
         + `<progress class="envio__barra" max="100" value="${pct}">${pct}%</progress></div>`;
  }

  /**
   * La foto desde la que sale el vuelo.
   *
   * Desde la rejilla, la que se está viendo —que en móvil puede ser la segunda.
   * Desde la ficha, la principal de la galería. Si no hay ninguna, `flyToCart`
   * se limita a hacer latir el icono del carrito, que es lo que de verdad
   * confirma; el vuelo sólo acompaña.
   */
  function origenDelVuelo(form) {
    const card = form.closest('.card');
    if (card) {
      return card.dataset.showing === 'secondary'
        ? card.querySelector('.card__img--secondary')
        : card.querySelector('.card__img--primary');
    }
    return document.querySelector('.pdp__media .pmedia img')
        || document.querySelector('.pmedia--foto img')
        || document.querySelector('.pmedia img');
  }

  /**
   * Cuánto ocupa la barra pegada de comprar, para no ponerse debajo de ella.
   * Devuelve 0 cuando no está o está escondida.
   */
  function altoDeLaBarra() {
    const barra = document.querySelector('[data-compra-pegada]');
    if (!barra || barra.hidden) return 0;
    const caja = barra.getBoundingClientRect();
    // Fuera de la ventana no estorba: aún no ha aparecido.
    return caja.height && caja.top < window.innerHeight ? caja.height : 0;
  }

  function pintarContador(total) {
    for (const n of document.querySelectorAll('[data-cart-count]')) {
      n.textContent = String(total);
      n.hidden = total === 0;
    }
  }

  function cerrar() {
    const viejo = document.getElementById(AVISO);
    if (viejo) viejo.remove();
  }

  function avisar(linea, total, moneda, bolsa) {
    cerrar();

    const caja = document.createElement('div');
    caja.id = AVISO;
    caja.className = 'agregado';
    // `alert` no: interrumpe al lector de pantalla a media frase. `status` lo
    // anuncia cuando termina lo que estaba diciendo, que es lo educado.
    caja.setAttribute('role', 'status');

    const foto = linea.image
      ? `<img class="agregado__img" src="${linea.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}" alt="" width="64" height="64">`
      : '';

    caja.style.setProperty('--agregado-abajo', altoDeLaBarra() + 'px');

    caja.innerHTML = `
      <button class="agregado__cerrar" type="button" aria-label="${t('a11y.close')}">&times;</button>
      ${foto}
      <div class="agregado__texto">
        <p class="agregado__estado">${selloDeTemporada()}${t('cart.added')}</p>
        <p class="agregado__nombre">${linea.product_title}</p>
        <p class="agregado__meta">${dinero(linea.final_line_price, moneda)}</p>
      </div>
      ${envioGratis(bolsa, moneda)}
      <div class="agregado__acciones">
        <button class="btn btn--ghost agregado__seguir" type="button">${t('cart.keep_shopping')}</button>
        <a class="btn btn--solid" href="/cart">${t('cart.view_cart', { count: total })}</a>
      </div>`;

    document.body.appendChild(caja);
    requestAnimationFrame(() => caja.setAttribute('data-visible', 'true'));

    caja.querySelector('.agregado__seguir').addEventListener('click', cerrar);
    caja.querySelector('.agregado__cerrar').addEventListener('click', cerrar);
    caja.addEventListener('keydown', (e) => { if (e.key === 'Escape') cerrar(); });
    caja.querySelector('.agregado__seguir').focus({ preventScroll: true });
  }

  /**
   * Los datos del formulario, CON el botón que lo ha enviado.
   *
   * ESTO ERA UN FALLO DE VENTA, no un detalle. `new FormData(form)` no incluye
   * al botón que dispara el envío — sólo lo hace el navegador al enviar de
   * verdad. Y en la compra rápida desde la rejilla la talla NO está en ningún
   * campo: está en `<button name="id" value="…">`, una por talla.
   *
   * Resultado: se pulsaba « M », salía el vuelo al carrito —porque el vuelo es
   * lo primero que pasa—, la petición viajaba SIN variante, Shopify la
   * rechazaba, y el plan B volvía a enviar el formulario… también sin el botón.
   * El cliente acababa en una página de error de Shopify:
   *
   *     Required parameter missing or invalid: items
   *
   * Desde la ficha nunca se vio, porque allí la variante sí es un campo del
   * formulario. Desde la rejilla —que es de donde más se compra en camisetas—
   * fallaba SIEMPRE.
   */
  function datosDe(form, quien) {
    const datos = new FormData(form);
    if (quien && quien.name && !datos.has(quien.name)) {
      datos.append(quien.name, quien.value || '');
    }
    return datos;
  }

  /**
   * El plan B, arreglado también.
   *
   * `form.submit()` es un envío por guion y tampoco lleva el botón: dejarlo tal
   * cual convertía un fallo de red en la misma página de error. Se le clava el
   * valor como campo oculto ANTES de enviar, y entonces el camino sin
   * JavaScript hace exactamente lo que haría el navegador.
   */
  function enviarADedo(form, quien) {
    if (quien && quien.name && !form.querySelector(`[name="${quien.name}"]:not(button)`)) {
      const campo = document.createElement('input');
      campo.type = 'hidden';
      campo.name = quien.name;
      campo.value = quien.value || '';
      form.appendChild(campo);
    }
    form.submit();
  }

  async function enviar(form, e) {
    const quien = e.submitter;
    const datos = datosDe(form, quien);
    const boton = (quien && quien.type === 'submit' ? quien : null)
      || form.querySelector('[name="add"], [type="submit"]');

    e.preventDefault();
    if (boton) boton.setAttribute('aria-busy', 'true');

    /* El vuelo sale YA, no cuando conteste el servidor: es la respuesta al dedo
       que acaba de pulsar, y esperar medio segundo a la red la convierte en un
       efecto suelto que nadie relaciona con lo que hizo. Si el añadido falla, el
       formulario se envía igual y la página cambia: no queda nada colgando. */
    flyToCart(origenDelVuelo(form), { boton });

    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST', body: datos, headers: { Accept: 'application/json' },
      });
      if (!r.ok) throw new Error('add');
      const linea = await r.json();

      const c = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const bolsa = c.ok ? await c.json() : { item_count: 0, currency: 'CHF' };

      pintarContador(bolsa.item_count);
      avisar(linea, bolsa.item_count, bolsa.currency || 'CHF', bolsa);
      document.dispatchEvent(new CustomEvent('carrito:cambiado', { detail: bolsa }));
    } catch {
      // Lo que no se pudo hacer por detrás, se hace por delante.
      enviarADedo(form, quien);
    } finally {
      if (boton) boton.removeAttribute('aria-busy');
    }
  }

  function initAgregar() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      enviar(form, e);
    });

    document.addEventListener('click', (e) => {
      const caja = document.getElementById(AVISO);
      if (caja && !caja.contains(e.target)) cerrar();
    });
  }

  return { datosDe, initAgregar };
})();

__EDI["components-vista-rapida.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  const { createLayer } = __EDI["core-a11y.js"];
  const { t } = __EDI["core-i18n.js"];
  const { price: formatPrice } = __EDI["core-format.js"];
  /**
   * La vista rápida: elegir color Y talla sin salir de la rejilla.
   *
   * POR QUÉ
   * -------
   * La compra rápida ya resuelve la pieza con UNA elección —la talla de una
   * camiseta negra—. Con dos (el sweat en Noir o Gris, S a 2XL; los t-shirts de
   * pareja) mandaba a la ficha, y eso es salir de la página para una decisión de
   * dos clics. Aquí se abre una capa con la foto, las dos elecciones y el botón.
   *
   * LO QUE NO SE REINVENTA
   * ----------------------
   * · La capa es la de siempre (`createLayer`): foco atrapado, Escape, fondo.
   * · El añadir es el de siempre: un `<form action="/cart/add">` con el `id` de
   *   la variante, que `agregar.js` intercepta como cualquier otro — mismo aviso,
   *   misma calavera en temporada, mismo contador.
   * · El producto sale de `/products/<handle>.js`, que Shopify sirve siempre.
   *
   * Y SI ALGO FALLA, EL ENLACE SIGUE SIENDO UN ENLACE: sin JavaScript, con
   * Ctrl/Cmd pulsado o si la petición falla, se va a la ficha como antes.
   */


  /* ── lógica pura: se prueba sin DOM ─────────────────────────────────────── */

  /** La variante que coincide con TODAS las opciones elegidas. */
  function elegirVariante(producto, seleccion) {
    return (producto?.variants ?? []).find((v) =>
      v.options.every((valor, i) => valor === seleccion[i])) ?? null;
  }

  /** Punto de partida: la primera variante disponible, o la primera a secas. */
  function seleccionInicial(producto) {
    const v = (producto?.variants ?? []).find((x) => x.available) ?? producto?.variants?.[0];
    return v ? [...v.options] : [];
  }

  /**
   * ¿Se puede elegir `valor` en la opción `indice`, con lo demás como está?
   * Un valor sin ninguna variante disponible con el resto de la selección sale
   * deshabilitado — no escondido: saber que la M existe y está agotada también
   * es información.
   */
  function valorDisponible(producto, seleccion, indice, valor) {
    return (producto?.variants ?? []).some((v) => v.available && v.options[indice] === valor
      && v.options.every((otro, i) => i === indice || otro === seleccion[i]));
  }

  /**
   * Al pulsar un valor: si con el resto de la selección no hay variante
   * disponible, se busca la disponible más parecida con ESE valor, para no dejar
   * al cliente en una combinación muerta (« Gris / 2XL » agotada → « Gris / XL »).
   */
  function aplicarValor(producto, seleccion, indice, valor) {
    const nueva = [...seleccion];
    nueva[indice] = valor;
    const exacta = elegirVariante(producto, nueva);
    if (exacta?.available) return nueva;
    const alternativa = (producto?.variants ?? [])
      .filter((v) => v.available && v.options[indice] === valor)
      .sort((a, b) => coincidencias(b, nueva) - coincidencias(a, nueva))[0];
    return alternativa ? [...alternativa.options] : nueva;
  }

  const coincidencias = (variante, seleccion) =>
    variante.options.reduce((n, valor, i) => n + (valor === seleccion[i] ? 1 : 0), 0);

  /** La foto de la variante si la tiene; si no, la del producto. */
  function fotoDe(producto, variante) {
    return variante?.featured_image?.src ?? producto?.featured_image ?? producto?.images?.[0] ?? null;
  }

  /* ── la capa ────────────────────────────────────────────────────────────── */

  const CERRAR = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">'
    + '<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>';

  let capa = null;
  let raiz = null;
  let pedido = 0;

  function tamano(url, ancho) {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', String(ancho));
      return url.startsWith('//') ? u.toString().replace(/^https?:/, '') : u.toString();
    } catch {
      return url;
    }
  }

  function crearCapa() {
    raiz = document.createElement('div');
    raiz.className = 'layer vista';
    raiz.dataset.open = 'false';
    raiz.setAttribute('role', 'dialog');
    raiz.setAttribute('aria-modal', 'true');
    raiz.setAttribute('aria-labelledby', 'vista-titulo');
    raiz.innerHTML = `
      <div class="layer__scrim" data-vista-cerrar></div>
      <div class="layer__panel layer__panel--modal vista__panel">
        <div class="layer__head">
          <p class="t-heading layer__title vista__titulo" id="vista-titulo"></p>
          <button class="layer__close" type="button" data-vista-cerrar aria-label="${t('a11y.close')}">${CERRAR}</button>
        </div>
        <div class="layer__body vista__cuerpo" data-vista-cuerpo></div>
      </div>`;
    document.body.append(raiz);
    capa = createLayer(raiz, { initialFocus: '.vista__valor:not([disabled]), .vista__anadir' });
    $$('[data-vista-cerrar]', raiz).forEach((b) => b.addEventListener('click', () => capa.close()));
    // Al añadir, `agregar.js` enseña su aviso: la capa ya ha cumplido.
    document.addEventListener('carrito:cambiado', () => capa?.close());
  }

  function pintar(producto) {
    let seleccion = seleccionInicial(producto);
    const cuerpo = $('[data-vista-cuerpo]', raiz);
    $('.vista__titulo', raiz).textContent = producto.title;

    const opciones = producto.options ?? [];
    cuerpo.innerHTML = `
      <div class="vista__rejilla">
        <figure class="vista__foto"><img alt="" width="600" height="600" decoding="async"></figure>
        <div class="vista__info">
          <p class="vista__precio" data-vista-precio></p>
          <form class="vista__form" action="/cart/add" method="post">
            <input type="hidden" name="id" value="">
            ${opciones.map((o, i) => `
              <fieldset class="vista__opcion">
                <legend class="t-label vista__leyenda">${escapar(o.name)} <span class="vista__elegido" data-vista-elegido="${i}"></span></legend>
                <div class="vista__valores">
                  ${o.values.map((v) => `<button class="vista__valor" type="button" data-indice="${i}" data-valor="${escapar(v)}" aria-pressed="false">${escapar(v)}</button>`).join('')}
                </div>
              </fieldset>`).join('')}
            <button class="btn btn--solid vista__anadir" type="submit" name="add"></button>
          </form>
          <a class="vista__ficha" href="${escapar(producto.url)}">${t('quickView.seeProduct')}<span aria-hidden="true"> &#8594;</span></a>
        </div>
      </div>`;

    const actualizar = () => {
      const v = elegirVariante(producto, seleccion);
      $('input[name="id"]', cuerpo).value = v?.id ?? '';
      const img = $('.vista__foto img', cuerpo);
      const foto = fotoDe(producto, v);
      if (foto) {
        img.src = tamano(foto, 600);
        img.srcset = `${tamano(foto, 400)} 400w, ${tamano(foto, 600)} 600w, ${tamano(foto, 900)} 900w`;
        img.sizes = '(min-width: 768px) 340px, 30vh';
      }
      const precio = $('[data-vista-precio]', cuerpo);
      const cents = v?.price ?? producto.price;
      const antes = v?.compare_at_price ?? null;
      precio.innerHTML = `${formatPrice(cents / 100)}${antes && antes > cents ? ` <s>${formatPrice(antes / 100)}</s>` : ''}`;

      $$('.vista__valor', cuerpo).forEach((b) => {
        const i = Number(b.dataset.indice);
        const activo = seleccion[i] === b.dataset.valor;
        b.setAttribute('aria-pressed', String(activo));
        b.disabled = !valorDisponible(producto, seleccion, i, b.dataset.valor);
      });
      $$('[data-vista-elegido]', cuerpo).forEach((s) => { s.textContent = seleccion[Number(s.dataset.vistaElegido)] ?? ''; });

      const boton = $('.vista__anadir', cuerpo);
      const vendible = Boolean(v?.available);
      boton.disabled = !vendible;
      boton.textContent = vendible
        ? t('quickView.add', { price: formatPrice(cents / 100) })
        : t(v ? 'quickView.soldOut' : 'quickView.unavailable');
    };

    $$('.vista__valor', cuerpo).forEach((b) => b.addEventListener('click', () => {
      seleccion = aplicarValor(producto, seleccion, Number(b.dataset.indice), b.dataset.valor);
      actualizar();
    }));
    actualizar();
  }

  function escapar(texto) {
    return String(texto ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  async function abrir(handle, enlace) {
    if (!raiz) crearCapa();
    const este = ++pedido;
    const cuerpo = $('[data-vista-cuerpo]', raiz);
    cuerpo.innerHTML = `<p class="vista__cargando" role="status">${t('quickView.loading')}</p>`;
    $('.vista__titulo', raiz).textContent = '';
    capa.open(enlace);
    try {
      const raizUrl = window.Shopify?.routes?.root ?? '/';
      const r = await fetch(`${raizUrl}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error(String(r.status));
      const producto = await r.json();
      if (este !== pedido) return;
      pintar(producto);
      $('.vista__valor[aria-pressed="true"]:not([disabled]), .vista__anadir', raiz)?.focus();
    } catch {
      // Lo que no se pudo enseñar aquí, se enseña en su página.
      capa.close();
      window.location.href = enlace.href;
    }
  }

  function initVistaRapida() {
    document.addEventListener('click', (e) => {
      const enlace = e.target.closest?.('a[data-vista-rapida]');
      if (!enlace) return;
      // Abrir en otra pestaña sigue siendo abrir en otra pestaña.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      abrir(enlace.dataset.vistaRapida, enlace);
    });
  }

  return { elegirVariante, seleccionInicial, valorDisponible, aplicarValor, fotoDe, initVistaRapida };
})();

__EDI["components-bienvenida.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  const { createLayer } = __EDI["core-a11y.js"];
  const { t } = __EDI["core-i18n.js"];
  /**
   * El saludo del −15 %.
   *
   * QUÉ HACE Y CUÁNDO
   * -----------------
   * Enseña UNA vez el cartel de bienvenida: a los segundos que diga el ajuste,
   * o antes si el ratón se va hacia la barra del navegador (quien se marcha es
   * a quien hay que decirle algo). En el móvil no hay ratón: se enseña cuando
   * se ha recorrido casi media página, que es la señal de que la tienda
   * interesa.
   *
   * LO QUE NO HACE, A PROPÓSITO
   * ---------------------------
   * · No sale dos veces. Quien lo cierra no lo vuelve a ver en X días; quien
   *   deja su correo no lo vuelve a ver nunca.
   * · No sale si ya se viene con un descuento puesto (`/discount/…`), ni
   *   encima de otra capa abierta —carrito, búsqueda, vista rápida—: dos
   *   ventanas a la vez es una de más.
   * · No secuestra la promesa: si el alta del correo falla, el código se enseña
   *   igual. Lo que puede fallar es la suscripción, no lo prometido.
   *
   * El estado vive en `localStorage`. Si el navegador lo bloquea —modo privado,
   * cookies capadas—, el cartel sale una vez por sesión y ya: molesto es
   * repetirlo, no enseñarlo.
   */


  const LLAVE = 'edisamp:bienvenida';
  const DIA = 86400000;

  /* ── lógica pura: se prueba sin DOM ─────────────────────────────────────── */

  /** Lee el estado guardado sin romperse con basura. */
  function leerEstado(crudo) {
    try {
      const d = JSON.parse(crudo);
      if (!d || typeof d !== 'object') return null;
      return { estado: String(d.estado ?? ''), ts: Number(d.ts) || 0 };
    } catch {
      return null;
    }
  }

  /**
   * ¿Toca enseñarlo?
   *
   * `suscrito` es para siempre: ya dio su correo y ya tiene el código.
   * `cerrado` caduca: a los `dias` que diga el comerciante se le puede volver a
   * ofrecer, porque una visita de hace un mes ya no recuerda el cartel.
   */
  function debeMostrar(estado, ahora, dias) {
    if (!estado) return true;
    if (estado.estado === 'suscrito') return false;
    if (estado.estado === 'cerrado') return ahora - estado.ts > dias * DIA;
    return true;
  }

  /**
   * ¿Se puede abrir AHORA?
   *
   * Dos memorias distintas, a propósito:
   *
   *   `cerradoSesion` dura lo que dura la visita. Quien cierra el cartel no
   *   quiere verlo otra vez al pasar a la siguiente página — pero mañana es
   *   otra visita y otro momento.
   *
   *   `estado` dura años. Ahí sólo se apunta quien YA dio su correo, y a ése no
   *   se le vuelve a ofrecer nunca: ya tiene el código.
   *
   * Franklin: « cada vez que un cliente entra siempre tiene que aparecer, por
   * si acaso; ya no tiene que salir para los que ya se suscribieron, porque no
   * le serviría de nada ». Con `dias` a 0, eso es exactamente esto.
   */
  function puedeAbrir(estado, cerradoSesion, ahora, dias) {
    if (cerradoSesion) return false;
    return debeMostrar(estado, ahora, dias);
  }

  /** La página ya trae un descuento puesto: no hay nada que ofrecer. */
  function vieneConDescuento(url) {
    const u = String(url || '');
    return /\/discount\//.test(u) || /[?&]discount=/.test(u);
  }

  /* ── el cartel ──────────────────────────────────────────────────────────── */

  function guardar(estado) {
    try {
      localStorage.setItem(LLAVE, JSON.stringify({ estado, ts: Date.now() }));
    } catch {
      /* Sin almacén: se queda en la sesión y ya está. */
    }
  }

  function leerGuardado() {
    try {
      return leerEstado(localStorage.getItem(LLAVE));
    } catch {
      return null;
    }
  }

  /* La visita, no el navegador: `sessionStorage` se vacía al cerrar la pestaña.
     Es la diferencia entre « ya lo he visto hoy » y « ya no me interesa ». */
  function cerradoEnSesion() {
    try {
      return sessionStorage.getItem(LLAVE) === 'cerrado';
    } catch {
      return false;
    }
  }

  function marcarCerradoEnSesion() {
    try {
      sessionStorage.setItem(LLAVE, 'cerrado');
    } catch {
      /* Sin almacén de sesión, el cartel puede repetirse dentro de la visita.
         Molesto, no roto. */
    }
  }

  function otraCapaAbierta(propia) {
    return $$('.layer[data-open="true"]').some((c) => c !== propia);
  }

  /**
   * El aviso de cookies de Shopify.
   *
   * Sale solo, en la primera visita, y se queda hasta que se responde. Si a los
   * ocho segundos el cartel se abre encima, el cliente ve DOS ventanas a la vez
   * —y la de abajo es la que la ley obliga a contestar—. Se espera a que no
   * esté: el saludo puede esperar, el consentimiento no.
   */
  function avisoCookiesDelante() {
    const b = document.querySelector('#shopify-pc__banner');
    if (!b) return false;
    const r = b.getBoundingClientRect();
    return r.height > 0 && getComputedStyle(b).visibility !== 'hidden';
  }

  function initBienvenida() {
    const raiz = $('[data-bienvenida]');
    if (!raiz) return;

    const dias = Number(raiz.dataset.dias) || 30;
    const retraso = (Number(raiz.dataset.retraso) || 8) * 1000;
    const premio = $('[data-bienvenida-premio]', raiz);
    const forma = $('form', raiz);
    const abrirYa = raiz.dataset.abrirYa === 'true';

    const capa = createLayer(raiz, { initialFocus: '#bienvenida-email, .bienvenida__ir' });
    if (!capa) return;

    let abierto = false;

    const cerrar = (guardando = true) => {
      if (guardando && !premio.hidden) guardar('suscrito');
      else if (guardando) guardar('cerrado');
      marcarCerradoEnSesion();
      capa.close();
    };

    let reintento = 0;
    const abrir = () => {
      if (abierto || capa.isOpen) return;
      if (otraCapaAbierta(raiz)) return;
      if (avisoCookiesDelante()) {
        // Se vuelve a mirar cada dos segundos, seis veces: si el cliente no
        // contesta al aviso en doce, no es momento de ofrecerle nada.
        if (reintento++ < 6) setTimeout(abrir, 2000);
        return;
      }
      abierto = true;
      capa.open();
    };

    $$('[data-bienvenida-cerrar]', raiz).forEach((b) => b.addEventListener('click', () => cerrar()));
    // La capa también se cierra con Escape y con el fondo: `createLayer` lo hace
    // solo, y entonces hay que dejar constancia igual que en el botón.
    raiz.addEventListener('transitionend', () => {
      if (!capa.isOpen && abierto) {
        guardar(premio.hidden ? 'cerrado' : 'suscrito');
        marcarCerradoEnSesion();
      }
    });

    // El premio: destapar el código y recordar que esta persona ya lo tiene.
    const destapar = () => {
      premio.hidden = false;
      forma?.setAttribute('hidden', '');
      guardar('suscrito');
      $('.bienvenida__ir', raiz)?.focus({ preventScroll: true });
    };

    forma?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const boton = $('.bienvenida__enviar', raiz);
      if (boton) boton.disabled = true;
      try {
        await fetch(forma.action, { method: 'POST', body: new FormData(forma), headers: { Accept: 'text/html' } });
      } catch {
        /* El alta es lo que puede fallar; la promesa no. */
      }
      destapar();
    });

    $('[data-bienvenida-copiar]', raiz)?.addEventListener('click', async (e) => {
      const codigo = $('[data-bienvenida-valor]', raiz)?.textContent.trim();
      if (!codigo) return;
      try {
        await navigator.clipboard.writeText(codigo);
        e.currentTarget.textContent = t('general.welcome.copied');
      } catch {
        // Sin portapapeles —Safari viejo, permiso denegado— se selecciona, que
        // es lo que hace falta para copiarlo a mano.
        const r = document.createRange();
        r.selectNodeContents($('[data-bienvenida-valor]', raiz));
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
    });

    if (abrirYa) {
      // Vino de un envío sin JavaScript que funcionó: el código ya está a la
      // vista y esto sólo lo pone delante.
      //
      // Y ANTES, ARRIBA. Shopify manda el formulario a `/contact#bienvenida-form`
      // y vuelve con esa ancla: el navegador aterriza en el PIE, que es donde
      // vive esta sección, y la tienda parece haberse ido al final. Se quita el
      // ancla de la barra y se sube. Franklin: « me baja hasta todo abajo ».
      if (location.hash) history.replaceState(null, '', location.pathname + location.search);
      window.scrollTo(0, 0);
      guardar('suscrito');
      abrir();
      return;
    }

    if (vieneConDescuento(location.href)) return;
    if (!puedeAbrir(leerGuardado(), cerradoEnSesion(), Date.now(), dias)) return;

    const porTiempo = setTimeout(abrir, retraso);

    // Salida del ratón por arriba: se va a cerrar la pestaña o a cambiar de
    // sitio. Es el único momento en que un cartel no interrumpe nada.
    const salida = (ev) => {
      if (ev.clientY <= 0) {
        clearTimeout(porTiempo);
        abrir();
      }
    };
    document.addEventListener('mouseout', salida);

    // Móvil: sin ratón, la señal es que se ha bajado casi media página.
    const porScroll = () => {
      const alto = document.documentElement.scrollHeight - innerHeight;
      if (alto > 0 && scrollY / alto > 0.45) {
        clearTimeout(porTiempo);
        abrir();
      }
    };
    addEventListener('scroll', porScroll, { passive: true });
  }

  return { leerEstado, debeMostrar, puedeAbrir, vieneConDescuento, initBienvenida };
})();

__EDI["components-cuenta-panel.js"] = (function () {
  'use strict';
  const { $, $$ } = __EDI["core-dom.js"];
  const { createLayer } = __EDI["core-a11y.js"];
  const wishlist = __EDI["core-wishlist.js"];
  /**
   * « Mon espace » — el panel del hombrecito.
   *
   * Abre y cierra con la misma capa que el carrito (`createLayer`: foco
   * atrapado, Escape cierra, el resto de la página queda inerte), y pinta los
   * favoritos desde el navegador, que es donde viven.
   *
   * MEJORA, NO CONDICIÓN. El hombrecito es un `<a href="/account">` de verdad.
   * Este módulo intercepta el clic para abrir el panel; si no llega a cargarse,
   * el enlace hace lo que hacía siempre.
   */


  /** Cuántos favoritos caben en el panel antes de mandar a la página. */
  const EN_PANEL = 4;

  /**
   * Una miniatura pequeña a partir de la URL que guardó el corazón.
   *
   * El corazón guarda la foto a 900 px —es lo que usa la página de favoritos—, y
   * en un panel de 80 px eso son varios cientos de KB por miniatura. Se pide la
   * misma foto a 160 px cambiando el parámetro, que es como sirve Shopify.
   */
  function miniatura(url) {
    if (!url) return null;
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', '160');
      return u.toString();
    } catch {
      return url;
    }
  }

  function pintarFavoritos(root) {
    const lista = $('[data-cuenta-fav-lista]', root);
    const vacio = $('[data-cuenta-fav-vacio]', root);
    const numero = $('[data-cuenta-fav-numero]', root);
    const todos = $('[data-cuenta-fav-todos]', root);
    if (!lista) return;

    const items = wishlist.all();
    lista.replaceChildren();

    for (const item of items.slice(0, EN_PANEL)) {
      const li = document.createElement('li');
      li.className = 'espacio__fav';

      const a = document.createElement('a');
      a.className = 'espacio__fav-enlace';
      a.href = item.url || `/products/${item.handle}`;

      const foto = miniatura(item.image);
      if (foto) {
        const img = document.createElement('img');
        img.className = 'espacio__fav-foto';
        img.src = foto;
        img.alt = '';
        img.width = 64;
        img.height = 80;
        img.loading = 'lazy';
        img.decoding = 'async';
        a.append(img);
      }

      const nombre = document.createElement('span');
      nombre.className = 'espacio__fav-nombre';
      // textContent y no innerHTML: el título viene de un atributo que pudo
      // tocar cualquiera, y un panel de cuenta no es sitio para inyectar nada.
      nombre.textContent = item.title || item.handle;
      a.append(nombre);

      li.append(a);
      lista.append(li);
    }

    const hay = items.length > 0;
    if (vacio) vacio.hidden = hay;
    if (numero) {
      numero.textContent = hay ? `(${items.length})` : '';
      numero.hidden = !hay;
    }
    if (todos) todos.hidden = !hay;
  }

  function initCuentaPanel() {
    const root = $('[data-cuenta]');
    if (!root) return;

    const layer = createLayer(root, { onOpen: () => pintarFavoritos(root) });

    $$('[data-cuenta-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        layer.open(e.currentTarget);
      })
    );

    $$('[data-cuenta-close]', root).forEach((b) => b.addEventListener('click', () => layer.close()));
    $('.layer__scrim', root)?.addEventListener('click', () => layer.close());

    // Si alguien quita un corazón con el panel abierto —o en otra pestaña—, la
    // lista se entera sin tener que cerrar y abrir.
    wishlist.onChange(() => pintarFavoritos(root));
    pintarFavoritos(root);
  }

  return { initCuentaPanel };
})();

__EDI["app.js"] = (function () {
  'use strict';
  const i18n = __EDI["core-i18n.js"];
  const { observeReveals } = __EDI["core-motion.js"];
  const { initMediaSure } = __EDI["core-media-sure.js"];
  const { initPrefetch } = __EDI["core-prefetch.js"];
  const { initCardPorte } = __EDI["components-card-porte.js"];
  const { initEntry3D } = __EDI["components-entry-3d.js"];
  const { initVuelo } = __EDI["components-vuelo.js"];
  const { initPastilla } = __EDI["components-pastilla.js"];
  const { initCuentaAtras } = __EDI["components-cuenta-atras.js"];
  const { initBusqueda } = __EDI["components-busqueda.js"];
  const { initPieza404 } = __EDI["components-pieza-404.js"];
  const { initWishlistCuenta } = __EDI["components-wishlist-cuenta.js"];
  const { initFavorito } = __EDI["components-favorito.js"];
  const { initFavoritosPuerta } = __EDI["components-favoritos-puerta.js"];
  const { initDecoradoReposo } = __EDI["components-decorado-reposo.js"];
  const { initAnuncios } = __EDI["components-anuncios.js"];
  const { initEscena } = __EDI["components-escena.js"];
  const { initHeader } = __EDI["components-header.js"];
  const { initNavSurface } = __EDI["components-nav-surface.js"];
  const { initCartDrawer } = __EDI["components-cart-drawer.js"];
  const { initAgregar } = __EDI["components-agregar.js"];
  const { initVistaRapida } = __EDI["components-vista-rapida.js"];
  const { initBienvenida } = __EDI["components-bienvenida.js"];
  const wishlist = __EDI["core-wishlist.js"];
  const { iniciarTemporada } = __EDI["experience-temporada.js"];
  const { initCuentaPanel } = __EDI["components-cuenta-panel.js"];
  /**
   * EDISAMP 2.0 — punto de entrada del storefront.
   *
   * Sólo orquesta, y sólo carga lo que la página tiene.
   *
   * Esto último era falso con imports estáticos: cada init se retiraba si no
   * encontraba su raíz, pero el navegador ya se había descargado el módulo
   * entero. La home pedía el selector de variantes, la guía de tallas, la
   * galería y la página de favoritos —29 ficheros— para no ejecutar ninguno.
   *
   * Ahora la raíz se busca ANTES de pedir el módulo. Lo que la página no tiene,
   * la página no lo descarga.
   */


  /**
   * Carga un módulo sólo si su raíz está en la página, y lo arranca.
   *
   * Un fallo al importar no puede tumbar el resto del arranque: cada pieza es
   * independiente y la página sigue siendo navegable sin ninguna de ellas.
   */
  async function cuando(selector, cargar, nombre) {
    if (!document.querySelector(selector)) return;
    try {
      const modulo = await cargar();
      // El export se nombra: coger «la primera función que haya» funciona hoy
      // porque cada módulo exporta una, y se rompe en silencio el día que
      // alguien exporte un ayudante antes que el init.
      const init = modulo[nombre];
      if (typeof init !== 'function') throw new Error(`sin export ${nombre}()`);
      await init();
    } catch (error) {
      if (MODE === 'development') console.warn(`[edisamp] ${selector}:`, error);
    }
  }

  const MODE = document.documentElement.dataset.mode ?? 'development';

  /** fr por defecto; ?lang=de|en|es para revisar los otros idiomas. */
  /**
   * Qué idioma se carga.
   *
   * En el prototipo lo elige el visitante y se recuerda: no hay servidor que
   * opine.
   *
   * En una tienda NO. Ahí el idioma lo decide Shopify —por el dominio, por el
   * prefijo de la URL, por Markets— y lo escribe en `<html lang>`. Si aquí
   * ganara `localStorage`, un cliente que cambia de idioma en la tienda vería
   * la página en alemán y los textos del JavaScript en francés, porque el
   * navegador se acuerda de la visita anterior. Se vio en la captura alemana:
   * « Grössentabelle » al lado de « Ajouter au panier ».
   *
   * Por eso el layout del tema declara `data-locale-servidor`. Donde está, el
   * servidor manda y no se discute.
   */
  function requestedLocale() {
    const raiz = document.documentElement;
    if (raiz.dataset.localeServidor !== undefined) {
      return raiz.lang || 'en';
    }
    const fromUrl = new URLSearchParams(location.search).get('lang');
    if (fromUrl) return fromUrl;
    try {
      return localStorage.getItem('edisamp:locale') ?? 'fr';
    } catch {
      return 'fr';
    }
  }

  async function boot() {
    document.documentElement.classList.remove('no-js');

    try {
      const locale = await i18n.init(requestedLocale());
      i18n.apply(document);
      try { localStorage.setItem('edisamp:locale', locale); } catch { /* modo privado */ }
    } catch (error) {
      // El HTML ya trae el texto en francés: sin diccionario el sitio sigue en pie.
      if (MODE === 'development') console.warn('[edisamp] i18n no disponible:', error);
    }
    // Quien esté esperando el diccionario (el editor de CUSTOM) debe poder
    // seguir aunque no haya llegado: mejor un texto en francés que un bloqueo.
    document.dispatchEvent(new CustomEvent('edisamp:i18n'));

    // Presentes en todas las páginas
    /* Antes que nada: tiene que estar oyendo antes de que se pinte la
       primera imagen, y antes de que el resto de módulos empiece a crear
       miniaturas. */
    initMediaSure();
    initPrefetch();
    initAgregar();
    initVistaRapida();
    initBienvenida();
    initHeader();
    initCardPorte();
    initEntry3D();
    initVuelo();
    initPastilla();
    initCuentaAtras();
    initCuentaPanel();
    iniciarTemporada();
    initBusqueda();
    initPieza404();
    initWishlistCuenta();
    initFavorito();
    initFavoritosPuerta();
    initDecoradoReposo();
    initAnuncios();
    initEscena();
    initNavSurface();
    initCartDrawer();
    // Delegado en el documento: las tarjetas se crean y destruyen al filtrar,
    // y un listener por tarjeta se quedaría huérfano.
    wishlist.mount(document);

    // Sólo donde exista su raíz — y sólo entonces se descarga.
    cuando('[data-hero]', () => import('./components-hero.js?v=e75f3c4023'), 'initHero');
    cuando('[data-story]', () => import('./components-product-story.js?v=0d8c4d3b79'), 'initProductStory');
    cuando('[data-gallery]', () => import('./components-product-gallery.js?v=8e55f0993b'), 'initGallery');
    cuando('[data-size-guide]', () => import('./components-size-guide.js?v=cb736e7e62'), 'initSizeGuide');
    cuando('[data-media-mixta]', () => import('./components-galeria-media.js?v=82d0cab6e4'), 'initGaleriaMedia');
    /* Dos anclas, un solo módulo: apunta en la ficha y pinta en la sección. */
    cuando('[data-apuntar-visto], [data-vistos]', () => import('./components-vistos.js?v=3b0c36f968'), 'initVistos');
    cuando('[data-compra-pegada]', () => import('./components-compra-pegada.js?v=9a2c82571c'), 'initCompraPegada');
    cuando('[data-comprable]', () => import('./components-comprable.js?v=a956e063d0'), 'initComprable');
    cuando('[data-carrusel]', () => import('./components-carrusel.js?v=199d51feff'), 'initCarrusel');
    cuando('[data-rapida]', () => import('./components-compra-rapida.js?v=7db45c6ec3'), 'initCompraRapida');
    cuando('[data-cantidad-caja]', () => import('./components-cantidad.js?v=563ee0970f'), 'initCantidad');
    cuando('[data-regalo-envio]', () => import('./components-regalo-envio.js?v=140bbc9aa1'), 'initRegaloEnvio');
    cuando('[data-wishlist-root]', () => import('./components-wishlist-page.js?v=ae05487bf5'), 'initWishlistPage');
    /* `[data-collection]` es el filtrado EN EL NAVEGADOR del prototipo. La
       colección del tema de Shopify no lleva ese atributo a propósito: allí
       filtra el servidor y este módulo dejaría la rejilla vacía —pregunta al
       catálogo, que en una tienda no responde—. Es la misma trampa del cajón
       del carrito, y aquí se evita no montándolo. */
    cuando('[data-collection]', () => import('./components-collection.js?v=4e1c8db100'), 'initCollection');
    cuando('[data-filtros]', () => import('./components-filtros.js?v=c2fd2ae8b5'), 'initFiltros');
    cuando('[data-cantidad]', () => import('./components-carrito.js?v=ff45916089'), 'initCarrito');
    cuando('[data-custom-catalog]', () => import('./custom-catalog.js?v=1fd4658ea2'), 'initCustomCatalog');
    cuando('[data-custom-entree]', () => import('./custom-entree.js?v=8190c77086'), 'initCustomEntree');
    cuando('[data-product]', () => import('./components-variant-selector.js?v=39799ee233'), 'initVariantSelector');

    observeReveals(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  return {  };
})();
